import {FormattedMessage, useIntl} from "react-intl";
import '../scss/Contacts.scss';
import React, {useEffect, useMemo, useRef, useState} from "react";
import {ReactComponent as UploadIcon} from "../svg/upload.svg";
import {GlobalFilter, TableRender} from "./SurveyRecipients";
import {GetAppTranslations, GetGeneralTranslations, GetSupportRoles, GetUsers, PostCodes} from "../api/APISurvey";
import {
    useFilters,
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
    useColumnOrder,
} from "react-table";
import {ReactComponent as First} from "../svg/first.svg";
import {ReactComponent as Previous} from "../svg/previous.svg";
import {ReactComponent as Next} from "../svg/next.svg";
import {ReactComponent as Last} from "../svg/last.svg";
import {ReactComponent as FileIcon} from "../svg/xls-file.svg";
import {ReactComponent as LoadingIcon} from "../svg/loading.svg";
import {ReactComponent as MoreIcon} from "../svg/more.svg";
import {ReactComponent as DownArrow} from "../svg/down-arrow.svg";
import {ReactComponent as UpArrow} from "../svg/up-arrow.svg";
import {ReactComponent as RefreshIcon} from "../svg/refresh.svg"
import {ReactComponent as SnackError} from "../svg/snack-error.svg";
import {ReactComponent as SnackInfo} from "../svg/snack-info.svg";
import {ReactComponent as SnackSuccess} from "../svg/snack-success.svg";
import {HoverTooltip, Popup, SlatSkeleton} from "../UIElements";
import {
    GetContactMeta,
    GetContactsAndRoles,
    GetSMSCountryCode, GetUserMeta, PostTranslation,
    PostUploadSave,
    PostUploadValidate,
} from "../api/APIContact";
import {Slat} from "./SurveySummary";
import {ReactComponent as Alert} from "../svg/alert.svg";
import {useSnackbar} from "../Content";
import {ReactComponent as CloseIcon} from "../svg/close.svg";
import {ReactComponent as BackArrow} from "../svg/back.svg";
import {useLanguageData, useLanguageOptionsData} from "../App";

import {
    calculateSlice,
    extractValuesByKey,
    formatMissingRows,
    FormatPhoneNumberDisplay, transformContactUploadDataForPost
} from "../Formats";
import {extractContactHeaders, findUserById} from "../DataFunctions";
import {AddUser} from "./Users";
import {PersonStanding} from "lucide-react";

export default function Contacts() {
    const intl = useIntl()
    const getUserData = GetUsers()
    const getSupportRolesData = GetSupportRoles()
    const getContactsAndRoles = GetContactsAndRoles()
    const getContactMeta = GetContactMeta()
    const getUserMeta = GetUserMeta()
    const getTranslations = GetAppTranslations(intl.locale)
    const getGeneralTranslations = GetGeneralTranslations(intl.locale)

    useEffect(()=>{
        document.title = intl.formatMessage({
            id:"main.sidebar.contacts",
            defaultMessage:"Contacts",
        }) + " | Annie Admin"
    },[])

    return <>
        <div className={"content"}>
            <div className={"header-container"}>
                <h1>
                    <PersonStanding size={24} strokeWidth={2} />
                    {intl.formatMessage(
                        {
                            id: 'main.sidebar.contacts',
                            defaultMessage: 'Contacts',
                        }
                    )}</h1>
            </div>
            {(getUserData.isLoading || getSupportRolesData.isLoading) &&
            <h2>{intl.formatMessage(
                {
                    id: 'loading',
                    defaultMessage: 'Loading...',
                })}</h2>
            }
            {
                getUserData.isSuccess && getSupportRolesData.isSuccess && getContactMeta.isSuccess && getUserMeta.isSuccess &&
                (() => {
                    const contactMetaData = getContactMeta?.data[0];
                    const contactHeaders = Object.keys(contactMetaData).sort((a, b) => {
                        const orderA = contactMetaData[a]?.order ?? Infinity
                        const orderB = contactMetaData[b]?.order ?? Infinity
                        return orderA - orderB
                    })

                    return (
                        <>
                            <UploadContacts
                                refetchContacts={() => {
                                    getContactsAndRoles.refetch()
                                    getContactMeta.refetch()
                                    getTranslations.refetch()
                                    getGeneralTranslations.refetch()
                                }}
                                userData={getUserData?.data}
                                supportRolesData={Object.keys(getSupportRolesData?.data[0])}
                                displayFullUpload={true}
                                contactMeta={contactMetaData}
                                contactHeaders={contactHeaders}
                                userMeta={getUserMeta?.data[0]}
                                refetchUsers={()=>getUserData.refetch()}
                            />
                            <ContactsRender
                                contactMeta={contactMetaData}
                                getContactsAndRoles={getContactsAndRoles}
                                supportRolesData={Object.keys(getSupportRolesData?.data[0])}
                                refetchContacts={() => getContactsAndRoles.refetch()}
                                refetchingContacts={getContactsAndRoles.isRefetching}
                                userData={getUserData?.data}
                            />
                        </>
                    )
                })()
            }
        </div>
    </>
}

const SUPPORTED_DOWNLOAD_LOCALES = ['en', 'fi', 'nl', 'es', 'sv', 'it', 'ca']

export function UploadContacts({addToSurvey, refetchUsers, addStudentsToSurvey, refetchContacts, userData, supportRolesData, displayFullUpload, contactMeta, contactHeaders, userMeta}) {
    const intl = useIntl()
    const downloadLocale = SUPPORTED_DOWNLOAD_LOCALES.includes(intl.locale) ? intl.locale : 'en'
    const downloadTemplateUrl = `/admin/downloads/${downloadLocale}/contact-example.xlsx`
    const fileInput = React.useRef(null)
    const dropZone = React.useRef(null)
    const [uploadFiles, setUploadFiles] = useState([])

    const handleContactUpload = e => {
        setUploadFiles(e.target.files)
    }

    const triggerFileInput = () => {
        fileInput.current.click()
    }

    const emptyFileInput = () => {
        setUploadFiles([])
        fileInput.current.value = null
    }

    const reTriggerFileInput = () => {
        fileInput.current.value = null
        fileInput.current.click()
    }

    const [dragOver, setDragOver] = useState(false)

    const handleDrop = e => {
        e.preventDefault()
        setDragOver(false)
        if (e.dataTransfer.items) {
            if (e.dataTransfer.items[0].kind === 'file') {
                const file = e.dataTransfer.items[0].getAsFile()
                if (file) {
                    setUploadFiles([e.dataTransfer.items[0].getAsFile()])
                }
            }
        } else {
            setUploadFiles(e.dataTransfer.files)
        }
    }

    const handleDragOver = e => {
        e.preventDefault()
        e.stopPropagation()
    }

    const handleDragEnter = e => {
        e.preventDefault()
        setDragOver(true)
    }

    const handleDragLeave = e => {
        e.preventDefault()
        setDragOver(false)
    }

    return <>
    {displayFullUpload ?
        <div className={dragOver ? "drop-zone drag-over" : "drop-zone"} ref={dropZone} onDrop={e=>handleDrop(e)} onDragOver={e=>handleDragOver(e)} onDragEnter={e=>handleDragEnter(e)} onDragLeave={e=>handleDragLeave(e)}>
            <UploadIcon />
            <p>
                <FormattedMessage
                    id={"contact.upload.dropOrChooseFile"}
                    defaultMessage={"<b>Drop or choose</b> a .xlsx file"}
                    values={{b: (...text) => <b>{text}</b>}}/>
            </p>
            <button aria-hidden={true} className={"main-button"} onClick={()=>triggerFileInput()}>
                <UploadIcon />
                {intl.formatMessage(
                    {
                        id: 'contact.uploadContacts',
                        defaultMessage: 'Upload students',
                    }
                )}
            </button>
            <a className={"download-template"} href={downloadTemplateUrl} download={true}>{intl.formatMessage({
                id:'contact.downloadTemplate',
                defaultMessage:"Download template"
            })}</a>
        </div> :
        <>
            <a className={"download-template"} href={downloadTemplateUrl} download={true}>{intl.formatMessage({
                id:'contact.downloadTemplate',
                defaultMessage:"Download template"
            })}</a>
            <button aria-hidden={true} className={"main-button"} onClick={()=>triggerFileInput()}>
                <UploadIcon />
                {intl.formatMessage(
                    {
                        id: 'uploadStudentXls',
                        defaultMessage: 'Upload student .xlsx',
                    }
                )}
            </button>
        </>
    }


        <label htmlFor={"contact-upload"} className={"hidden-element"}>
            {intl.formatMessage(
                {
                    id: 'contact.uploadContacts',
                    defaultMessage: 'Upload students',
                }
            )}
        </label>
        <input type={"file"} ref={fileInput} id={"contact-upload"}
               className={"hidden-element"} name={"contact-upload"}
               onChange={e => handleContactUpload(e)}
               accept={".xlsx, text/xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"}/>
        {
            uploadFiles.length > 0 &&
            <UploadPopup uploadFile={uploadFiles[0]}
                         emptyFileInput={()=>emptyFileInput()}
                         reTriggerFileInput={()=>reTriggerFileInput()}
                         setNewUploadFiles={files=>setUploadFiles(files)}
                         addToSurvey={addToSurvey}
                         addStudentsToSurvey={data=>addStudentsToSurvey(data)}
                         refetchContacts={()=>refetchContacts()}
                         userData={userData}
                         supportRolesData={supportRolesData}
                         contactMeta={contactMeta}
                         contactHeaders={contactHeaders}
                         userMeta={userMeta}
                         refetchUsers={refetchUsers}
            />
        }
    </>
}

function UploadPopup({uploadFile, refetchUsers, emptyFileInput, reTriggerFileInput, setNewUploadFiles, addToSurvey, addStudentsToSurvey, refetchContacts, userData, supportRolesData, contactMeta, contactHeaders, userMeta}) {
    const intl = useIntl()
    const downloadLocale = SUPPORTED_DOWNLOAD_LOCALES.includes(intl.locale) ? intl.locale : 'en'
    const downloadTemplateUrl = `/admin/downloads/${downloadLocale}/contact-example.xlsx`

    const [fileToValidate, setFileToValidate] = useState(uploadFile)
    const [validateData, setValidateData] = useState({})

    // Check for roles
    const annieUsers = userData.map(user => user?.id)
    const extraColumns = validateData?.extracolumn

    const getSMSCountryCode = GetSMSCountryCode()
    const postUploadValidate = PostUploadValidate()
    const postUploadSave = PostUploadSave()

    const loading = postUploadValidate.isLoading || getSMSCountryCode.isLoading
    const error = postUploadValidate.isError || getSMSCountryCode.isError
    const success = postUploadValidate.isSuccess && getSMSCountryCode.isSuccess
    const error400 = postUploadValidate.isError && postUploadValidate.error.response.status === 400

    const [showStudentTable, setShowStudentTable] = useState(false)
    const [studentTableData, setStudentTableData] = useState([])

    const checkFile = () => {
        let uploadFileData = new FormData()
        uploadFileData.append("fileup", fileToValidate, fileToValidate.name)

        postUploadValidate.mutate(uploadFileData, {
            onError: error => {
                console.log(error)
            },
            onSuccess:data => {
                setValidateData(data.data)
            }
        })
    }

    useEffect(() => {
        setFileToValidate(uploadFile)
    },[uploadFile])

    useEffect(() => {
        checkFile()
    },[fileToValidate])

    const {setSnackbarData} = useSnackbar()

    const uploadData = {
        contacts: validateData?.contacts,
        extracolumn: validateData?.extracolumn
    }

    const [rolesForMapping, setRolesForMapping] = useState(supportRolesData)
    const [contactMetaForMapping, setContactMetaForMapping] = useState(contactHeaders)

    const { instanceLangOptions } = useLanguageOptionsData()

    const roleNameDefaults = {}
    supportRolesData?.forEach(role => {
        roleNameDefaults[role] = {}

        instanceLangOptions?.forEach(lang => {
            roleNameDefaults[role][lang] = ""
        })
    })

    const [roleNames, setRoleNames] = useState(roleNameDefaults)

    const getDefaultColumn = meta => {
        return extraColumns && Object.keys(extraColumns)?.includes(meta) ? meta : ''
    }

    const columnDefaults = {}
    contactHeaders?.forEach(meta => {
        columnDefaults[meta] = {}

        columnDefaults[meta]["column"] = getDefaultColumn(meta)

        instanceLangOptions?.forEach(lang => {
            columnDefaults[meta][lang] = ""
        })
    })

    const [metaColumnNames, setMetaColumnNames] = useState(columnDefaults)

    function setDetails(details) {
        let newDetailNames = metaColumnNames
        // Add new detail to meta columns if doesnt exist
        for (let i = 0; i < details?.length; i++) {
            const detail = details[i]
            if (!metaColumnNames.hasOwnProperty(detail)) {
                metaColumnNames[detail] = {}
                for (let i = 0; i < instanceLangOptions?.length; i++) {
                    const lang = instanceLangOptions[i]
                    newDetailNames[detail][lang] = ""
                }
                break
            }
        }
        // remove key from meta columns if deleted
        const metaColumnKeys = Object.keys(metaColumnNames)
        for (let i = 0; i < metaColumnKeys?.length; i++) {
            const metaKey = metaColumnKeys[i]
            if (!details.includes(metaKey)) {
                delete newDetailNames[metaKey]
                break
            }
        }

        setContactMetaForMapping(details)
        setMetaColumnNames(newDetailNames)
    }

    function setRoles(roles) {
        let newRoleNames = roleNames
        // Add new role to roleNames if doesnt exist
        for (let i = 0; i < roles?.length; i++) {
            const role = roles[i]
            if (!roleNames.hasOwnProperty(role)) {
                newRoleNames[role] = {}
                for (let i = 0; i < instanceLangOptions?.length; i++) {
                    const lang = instanceLangOptions[i]
                    newRoleNames[role][lang] = ""
                }
                break
            }
        }
        // remove key from roleNames if deleted
        const roleNamesKeys = Object.keys(roleNames)
        for (let i = 0; i < roleNamesKeys?.length; i++) {
            const roleKey = roleNamesKeys[i]
            if (!roles.includes(roleKey)) {
                delete newRoleNames[roleKey]
                break
            }
        }
        setRolesForMapping(roles)
        setRoleNames(newRoleNames)
    }

    const newCustomRoles = Object.keys(roleNames)?.length > supportRolesData?.length
    const newCustomMeta = Object.keys(metaColumnNames)?.length > contactHeaders?.length

    const postCodes = PostCodes()
    const postTranlations = PostTranslation()

    // create an error object to modify
    const errorCheckDefaultObject = [...supportRolesData, ...contactHeaders].reduce((acc, key) => ({ ...acc, [key]: false }), {})

    const [errorCheckObject, setErrorCheckObject] = useState(errorCheckDefaultObject)

    const errorCheckFailed = Object.values(errorCheckObject).some(value => value === false)

    const postRolesAndStudents = (andToSurvey) => {
        if (newCustomRoles || newCustomMeta) {
            const filterCustomKeys = (newData, oldData) => Object.keys(newData).filter(key => !oldData.includes(key))
            const customRoles = filterCustomKeys(roleNames, supportRolesData)
            const customMeta = filterCustomKeys(metaColumnNames, contactHeaders)
            const createRoleData = () => {
                let missingRoleNames = false
                const roleDataForPost = customRoles.map(role => ({
                    codeset: "role",
                    code: role,
                    value: {
                        "group": "custom"
                    }
                }))
                const roleNamesForPost = customRoles.flatMap(role =>
                        instanceLangOptions.map(lang => {
                            const trimmedName = roleNames[role][lang].trim()
                            if (trimmedName.length < 1) {
                                missingRoleNames = true
                            }
                            return {
                                segment: "general",
                                field: `role.${role}`,
                                language: lang,
                                value: trimmedName
                            }
                        })
                )
                return {roleDataForPost, roleNamesForPost, missingRoleNames}
            }
            const createMetaData = () => {
                let missingMetaNames = false
                const metaDataForPost = customMeta.map(meta => ({
                    codeset: "contactmeta",
                    code: meta,
                    value: {
                        "type": "text",
                        "required": false,
                        "appVisible": false
                    }
                }))
                const metaNamesForPost = customMeta.flatMap(meta =>
                    instanceLangOptions.map(lang => {
                        const trimmedName = metaColumnNames[meta][lang].trim()
                        if (trimmedName.length < 1) {
                            missingMetaNames = true
                        }
                        return {
                            segment: "general",
                            field: `contactmeta.${meta}`,
                            language: lang,
                            value: trimmedName
                        }
                    })
                )
                return {metaDataForPost, metaNamesForPost, missingMetaNames}
            }

            const {roleDataForPost, roleNamesForPost, missingRoleNames} = createRoleData()
            const {metaDataForPost, metaNamesForPost, missingMetaNames} = createMetaData()

            const combinedDataForPost = [...roleDataForPost, ...metaDataForPost]
            const combinedNamesForPost = [...roleNamesForPost, ...metaNamesForPost]

            if (!missingRoleNames && !missingMetaNames) {
                // Add translations
                postTranlations.mutate(combinedNamesForPost, {
                    onError: error => {
                        setSnackbarData({
                            show: true,
                            text: intl.formatMessage(
                                {
                                    id: 'addingNewFields.failed',
                                    defaultMessage: 'Adding new fields failed. Please try again.',
                                }),
                            state: "error",
                            autoHide: true,
                        })
                        console.log(error)
                    },
                    onSuccess:() => {
                        // Add to codes
                        postCodes.mutate(combinedDataForPost, {
                            onError: error => {
                                setSnackbarData({
                                    show: true,
                                    text: intl.formatMessage(
                                        {
                                            id: 'addingNewFields.failed',
                                            defaultMessage: 'Adding new fields failed. Please try again.',
                                        }),
                                    state: "error",
                                    autoHide: true,
                                })
                                console.log(error)
                            },
                            onSuccess:() => {
                                addStudents(andToSurvey)
                            }
                        })
                    }
                })
            } else {
                if (missingRoleNames) {
                    alert(intl.formatMessage({id:"alert.missingRoleNames", defaultMessage:"Some roles are missing names. Please type them to proceed."}))
                } else {
                    alert(intl.formatMessage({id:"alert.missingMetaNames", defaultMessage:"Some details are missing names. Please type them to proceed."}))
                }
            }
        } else {
            addStudents(andToSurvey)
        }
    }

    const addStudents = (andToSurvey) => {
        if (!errorCheckFailed) {
            const saveData = transformContactUploadDataForPost(uploadData, metaColumnNames)

            postUploadSave.mutate(saveData, {
                onError: error => {
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage(
                            {
                                id: 'contact.upload.saveFailed',
                                defaultMessage: 'Save failed. Please try again.',
                            }),
                        state: "error",
                        autoHide: true,
                    })
                    console.log(error)
                },
                onSuccess:data => {
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage(
                            {
                                id: 'contact.upload.saveSuccess',
                                defaultMessage: 'Students added.',
                            }),
                        state: "success",
                        autoHide: true,
                    })
                    andToSurvey && addStudentsToSurvey(data.hasOwnProperty("data") ? data.data["insertids"].concat(data.data["updateids"]) : [])
                    refetchContacts()
                    emptyFileInput()
                }
            })
        } else {
            alert("Data not validated")
        }
    }

    const fileHasStudentData = validateData?.contacts?.length > 0 || validateData?.errors?.length > 0

    if (uploadFile) {
        return <Popup closePopup={()=>emptyFileInput()} className={"full-width"}>
            <FileDisplayAndUpload currentFile={uploadFile.name} reTriggerFileInput={()=>reTriggerFileInput()} setNewUploadFiles={files=>setNewUploadFiles(files)} validating={loading}/>
            {!showStudentTable &&
            <div className={"upload-results"}>
                {loading &&
                <>
                    <h2>{intl.formatMessage(
                        {
                            id: 'contact.upload.checkingFile',
                            defaultMessage: 'Checking file...',
                        })}</h2>
                    <div className={"slat-container"}>
                        <FileCheckSkeleton />
                    </div>
                </>
                }
                {
                    error &&
                    <>
                        <h2 className={"error"}>
                            {intl.formatMessage(
                                {
                                    id: 'contact.upload.fileCheckFailed',
                                    defaultMessage: 'File check failed. Please try again.',
                                })}
                        </h2>
                        {error400 &&
                        <>
                            <p className={"error-description"}>
                                <FormattedMessage
                                    id={"contact.upload.fileCheck400"}
                                    defaultMessage={"Make sure that the uploaded .xlsx-file resembles the provided template.{br}NB: the data tab has to be named anniecontact"}
                                    values={{br: <br/>}}/>
                            </p>
                            <a className={"download-template"} href={downloadTemplateUrl} download={true}>{intl.formatMessage({
                                id:'contact.downloadTemplate',
                                defaultMessage:"Download template"
                            })}</a>
                        </>
                        }
                    </>
                }
                {success && !fileHasStudentData &&
                    <>
                        <h2 className={"error"}>
                            {intl.formatMessage(
                                {
                                    id: 'contact.upload.fileCheckFailed',
                                    defaultMessage: 'File check failed. Please try again.',
                                })}
                        </h2>
                        <p className={"error-description"}>
                            {intl.formatMessage({
                                id:"uploadedStudentFileHasNoData.error",
                                defaultMessage: "Uploaded file doesn\'t have student data. Please check the file and upload again."
                            })}
                        </p>
                    </>
                }
                {success && fileHasStudentData && !showStudentTable &&
                    <>
                        <FileInfo data={validateData} setShowStudentTable={()=>setShowStudentTable(true)} setStudentTableData={(data)=>setStudentTableData(data)}/>
                        <DefineColumns data={validateData}
                                     setValidateData={e => setValidateData(e)}
                                     supportRolesData={supportRolesData}
                                     rolesForMapping={rolesForMapping}
                                     setRolesForMapping={roles => setRoles(roles)}
                                     roleNames={roleNames}
                                     setRoleNames={roles => setRoleNames(roles)}
                                       contactMetaForMapping={contactMetaForMapping}
                                       setContactMetaForMapping={columns => setDetails(columns)}
                                       contactMeta={contactMeta}
                                       contactHeaders={contactHeaders}
                                       metaColumnNames={metaColumnNames}
                                       setMetaColumnNames={columns => setMetaColumnNames(columns)}
                                       countryCode={getSMSCountryCode.data[0].value}
                                       userData={userData}
                                       errorCheckObject={errorCheckObject}
                                       setErrorCheckObject={value => setErrorCheckObject(value)}
                                       userMeta={userMeta}
                                       refetchUsers={refetchUsers}
                        />
                    </>
                }
            </div>
            }
            {success && fileHasStudentData && showStudentTable &&
                <StudentTable tableData={studentTableData} hideStudentTable={()=>setShowStudentTable(false)} supportRolesData={supportRolesData}/>
            }
            <div className={"options"}>
                {!loading && errorCheckFailed &&
                <p className={"alert red"}>
                    <Alert />
                    {intl.formatMessage(
                        {
                            id: 'contact.upload.fixErrors',
                            defaultMessage: 'Fix errors before adding students.',
                        })}
                </p>
                }
                {postUploadSave.isLoading &&
                <p className={"loading"}>
                    <LoadingIcon />
                    {intl.formatMessage(
                        {
                            id: 'contact.upload.saving',
                            defaultMessage: 'Saving students...',
                        })}
                </p>
                }
                <button className={"cancel"} onClick={()=>emptyFileInput()}>
                    {intl.formatMessage({
                        id: 'modal.cancel',
                        defaultMessage: 'Cancel'})}
                </button>
                {!addToSurvey ?
                <>
                    {loading || errorCheckFailed || postUploadSave.isLoading || !fileHasStudentData ?
                        <button className={"confirm"} disabled>
                            {intl.formatMessage({
                                id: 'modal.upload',
                                defaultMessage: 'Add students'
                            })}
                        </button> :
                        <button className={"confirm"} onClick={() => postRolesAndStudents()}>
                            {intl.formatMessage({
                                id: 'modal.upload',
                                defaultMessage: 'Add students'
                            })}
                        </button>
                    }
                </> :
                    <>
                        {loading || errorCheckFailed || postUploadSave.isLoading || !fileHasStudentData ?
                            <button className={"confirm add"} disabled>
                                <UpArrow/>
                                {intl.formatMessage({
                                    id: 'modal.uploadAndAdd',
                                    defaultMessage: 'Add students to survey'
                                })}
                            </button> :
                            <button className={"confirm add"} onClick={() => postRolesAndStudents(true)}>
                                <UpArrow/>
                                {intl.formatMessage({
                                    id: 'modal.uploadAndAdd',
                                    defaultMessage: 'Add students to survey'
                                })}
                            </button>
                        }
                    </>
                }
            </div>
        </Popup>
    }

    return null
}

export function StudentTable({tableData, hideStudentTable, supportRolesData}) {
    const intl = useIntl()
    const contactHeaders = extractContactHeaders(tableData)

    const headerName = (column) => {
        return intl.formatMessage({
            id: `contactmeta.${column}`,
            defaultMessage: column
        })
    }

    const columnArray = contactHeaders.map( header => {
        return {
            Header: headerName(header),
            accessor: header,
            width: 200,
            Cell: ({ value }) => {
                return value != null ? String(value) : ""
            }
        }
    })

    const data = useMemo(() => tableData, [tableData])
    const columns = useMemo(()=> columnArray, [columnArray])

    return <div className={"student-table"}>
        <button className={"text back"} onClick={()=>hideStudentTable()}><BackArrow /> {intl.formatMessage({
            id: "back",
            defaultMessage:"Back"
        })}</button>
        <ContactTable columns={columns} data={data} contactHeaders={contactHeaders} supportRolesData={supportRolesData}/>
    </div>
}

export function FileDisplayAndUpload({currentFile, setNewUploadFiles, reTriggerFileInput, validating}) {
    const dropZone = React.useRef(null)

    const [dragOver, setDragOver] = useState(false)
    const intl = useIntl()

    const handleDrop = e => {
        e.preventDefault()
        setDragOver(false)
        if (e.dataTransfer.items) {
            if (e.dataTransfer.items[0].kind === 'file') {
                const file = e.dataTransfer.items[0].getAsFile()
                if (file) {
                    setNewUploadFiles([e.dataTransfer.items[0].getAsFile()])
                }
            }
        } else {
            setNewUploadFiles(e.dataTransfer.files)
        }
    }

    const handleDragOver = e => {
        e.preventDefault()
        e.stopPropagation()
    }

    const handleDragEnter = e => {
        e.preventDefault()
        setDragOver(true)
    }

    const handleDragLeave = e => {
        e.preventDefault()
        setDragOver(false)
    }

    return <>
        <div className={dragOver ? "drag-over file-upload" : "file-upload"} ref={dropZone} onDrop={e=>handleDrop(e)} onDragOver={e=>handleDragOver(e)} onDragEnter={e=>handleDragEnter(e)} onDragLeave={e=>handleDragLeave(e)}>
            <FileIcon/>
            <h1>{currentFile}</h1>
            {validating ?
                <button disabled={true} className={"default"}>
                    <UploadIcon />
                    {intl.formatMessage(
                        {
                            id: 'contact.uploadOther',
                            defaultMessage: 'Change file'
                        }
                    )}
                </button> :
                <button className={"default"} onClick={()=>reTriggerFileInput()}>
                    <UploadIcon />
                    {intl.formatMessage(
                        {
                            id: 'contact.uploadOther',
                            defaultMessage: 'Change file'
                        }
                    )}
                </button>
            }

        </div>
        </>
}

export function FileCheckSkeleton() {
    return <div className={"slat-container"}>
        <SlatSkeleton />
        <SlatSkeleton />
        <SlatSkeleton />
        <SlatSkeleton />
        <SlatSkeleton />
        <SlatSkeleton />
    </div>
}

export function FileInfo({data, setShowStudentTable, setStudentTableData, userInfo}) {
    const contacts = userInfo ? data.annieusers.length + data.errors.length : data.contacts.length + data.errors.length
    const newContacts = data.meta["insert"]
    const errorContacts = data.meta["errors"]
    const existingContacts = data.meta["update"]
    const intl = useIntl()
    const expandMeta = data => {
        return data.map(user => {
            if (user.hasOwnProperty("meta")) {
                const metaKeys = Object.keys(user.meta)
                for (let i = 0; i < metaKeys.length; i++) {
                    const key = user.meta[metaKeys[i]]
                    if (!user.hasOwnProperty(metaKeys[i])) {
                        user[metaKeys[i]] = key
                    }
                }
                delete user.meta
            }
            return user
        })
    }
    const allData = userInfo ? expandMeta(data["errors"].concat(data["annieusers"]).map(user => user.hasOwnProperty("data") && user["data"])) : data["errors"].concat(data["contacts"]).map(contact => contact.hasOwnProperty("data") && contact["data"])
    const newData = userInfo ? expandMeta(data["annieusers"].filter(e => e.insert).map(user => user.hasOwnProperty("data") && user["data"])) : data["contacts"].filter(e => e.insert).map(contact => contact.hasOwnProperty("data") && contact["data"])
    const updateData = userInfo ? expandMeta(data["annieusers"].filter(e => !e.insert).map(user => user.hasOwnProperty("data") && user["data"])) : data["contacts"].filter(e => !e.insert).map(contact => contact.hasOwnProperty("data") && contact["data"])
    const errorData = userInfo ? expandMeta(data["errors"].map(contact => contact.hasOwnProperty("data") && contact["data"])) : data["errors"].map(contact => contact.hasOwnProperty("data") && contact["data"])

    const showStudentTable = type => {
        switch (type) {
            case "all":
                setStudentTableData(allData)
                break
            case "new":
                setStudentTableData(newData)
                break
            case "updates":
                setStudentTableData(updateData)
                break
            case "errors":
                setStudentTableData(errorData)
                break
            default:

        }
        setShowStudentTable()
    }

    const amountInFileText = userInfo ? intl.formatMessage(
            {
                id: 'users.upload.usersInFile',
                defaultMessage: 'users in file',
            }) : intl.formatMessage(
        {
            id: 'contact.upload.studentsInFile',
            defaultMessage: 'students in file',
        })

    return <div className={"file-info"}>
        {contacts < 1 ?
            <p><span className={"number"}>{contacts}</span> {amountInFileText}</p> :
            <button onClick={()=>showStudentTable("all")}><span className={"number"}>{contacts}</span> {amountInFileText}</button>
        }
        <div className={"details"}>
            {newContacts < 1 ?
                <p className={newContacts < 1 ? "none" : "green"}><span className={"number"}>{newContacts}</span> {intl.formatMessage(
                    {
                        id: 'contact.upload.inFile.new',
                        defaultMessage: 'new',
                    })}</p> :
                <button onClick={()=>showStudentTable("new")} className={newContacts < 1 ? "none" : "green"}><span className={"number"}>{newContacts}</span> {intl.formatMessage(
                    {
                        id: 'contact.upload.inFile.new',
                        defaultMessage: 'new',
                    })}</button>
            }
            {existingContacts < 1 ?
                <p className={existingContacts < 1 ? "none" : "blue"}><span className={"number"}>{existingContacts}</span> {intl.formatMessage(
                    {
                        id: 'contact.upload.inFile.updates',
                        defaultMessage: 'updates',
                    })}</p> :
                <button onClick={()=>showStudentTable("updates")} className={existingContacts < 1 ? "none" : "blue"}><span className={"number"}>{existingContacts}</span> {intl.formatMessage(
                    {
                        id: 'contact.upload.inFile.updates',
                        defaultMessage: 'updates',
                    })}</button>
            }
            {errorContacts < 1 ?
                <p className={errorContacts < 1 ? "none" : "red"}><span className={"number"}>{errorContacts}</span> {intl.formatMessage(
                    {
                        id: 'contact.upload.inFile.errors',
                        defaultMessage: 'with errors',
                    })}</p> :
                <button onClick={()=>showStudentTable("errors")} className={errorContacts < 1 ? "none" : "red"}><span className={"number"}>{errorContacts}</span> {intl.formatMessage(
                    {
                        id: 'contact.upload.inFile.errors',
                        defaultMessage: 'with errors',
                    })}</button>
            }
        </div>
    </div>
}

// Normalize string to match extracolumn keys (spaces removed, Scandinavian letters converted)
const normalizeColumnName = (str) => {
    if (!str) return ''
    return str
        .toLowerCase()
        .replace(/\s/g, '')
        .replace(/ä/g, 'a')
        .replace(/ö/g, 'o')
        .replace(/å/g, 'a')
}

function DefineColumns({data, countryCode, refetchUsers, userData, errorCheckObject, setErrorCheckObject, setValidateData, supportRolesData, rolesForMapping, setRolesForMapping, roleNames, setRoleNames, contactMetaForMapping, setContactMetaForMapping, contactMeta, contactHeaders, metaColumnNames, setMetaColumnNames, userMeta }) {
    const intl = useIntl()
    const extraColumns = Object.keys(data?.extracolumn)
    const definedColumns = extraColumns.filter(column => data?.extracolumn[column])
    const contacts = data?.contacts

    const sortedColumnHeaders = contactMetaForMapping.sort((a, b) => {
        const requiredA = contactMeta[a]?.required ?? false
        const requiredB = contactMeta[b]?.required ?? false
        const orderA = contactMeta[a]?.order ?? Infinity
        const orderB = contactMeta[b]?.order ?? Infinity

        if (requiredA && !requiredB) {
            return -1
        }
        if (!requiredA && requiredB) {
            return 1
        }

        return orderA - orderB
    })

    // Assign columns to student info automatically if possible
    useEffect(() => {
        const updatedMetaColumnNames = { ...metaColumnNames }
        const metaColumnKeys = Object.keys(metaColumnNames)
        const translations = {}
        const normalizedMetaKeys = {}

        // Build normalized lookup for meta column keys
        for (const key of metaColumnKeys) {
            normalizedMetaKeys[normalizeColumnName(key)] = key
        }

        // Build normalized lookup for translations
        for (const key of metaColumnKeys) {
            const translation = intl?.messages[key]
            if (translation) {
                translations[normalizeColumnName(translation)] = key
            }
        }

        for (const column of extraColumns) {
            const normalizedColumn = normalizeColumnName(column)

            // Check if the column name matches any meta column name (normalized)
            if (normalizedMetaKeys[normalizedColumn]) {
                const metaKey = normalizedMetaKeys[normalizedColumn]
                updatedMetaColumnNames[metaKey]['column'] = column
            } else if (translations[normalizedColumn]) {
                // Check if the column name matches any translated meta column name (normalized)
                const meta = translations[normalizedColumn]
                updatedMetaColumnNames[meta]['column'] = column
            }
        }

        setMetaColumnNames(updatedMetaColumnNames)
    }, [])

    // assign data to roles automatically if possible
    useEffect(() => {
        const updatedData = {...data}
        const translations = {}
        const normalizedRoles = {}

        // Construct normalized translations map with original case roles
        for (const role of rolesForMapping) {
            const translation = intl?.messages["role."+role]
            if (translation) {
                translations[normalizeColumnName(translation)] = role
            }
            // Also map normalized role name to original
            normalizedRoles[normalizeColumnName(role)] = role
        }

        // Iterate through extracolumn
        for (const column of Object.keys(updatedData.extracolumn)) {
            const normalizedColumn = normalizeColumnName(column);

            // Check if the column name matches any translated role for mapping (normalized)
            if (translations[normalizedColumn]) {
                updatedData.extracolumn[column] = translations[normalizedColumn];
                continue;
            }

            // Check if the column name matches any role for mapping (normalized)
            if (normalizedRoles[normalizedColumn]) {
                updatedData.extracolumn[column] = normalizedRoles[normalizedColumn];
            }
        }

        setValidateData(updatedData);
    }, []);

    function checkIfDetailExists(detail) {
        if (contactMetaForMapping.includes(detail)) {
            const prefix = "CUSTOM"
            const newIndex = parseInt(detail.substring(prefix.length), 10) + 1
            const newDetail = prefix + newIndex
            return checkIfDetailExists(newDetail)
        } else {
            return detail
        }
    }

    const addCustomDetail = () => {
        const customDetails = contactMetaForMapping.filter(detail => detail.startsWith("CUSTOM"))
        const newDetailIndex = customDetails?.length + 1
        const newDetail = checkIfDetailExists("CUSTOM" + newDetailIndex)
        const newDetailsForMapping = [...contactMetaForMapping]
        newDetailsForMapping.push(newDetail)
        setContactMetaForMapping(newDetailsForMapping)
    }

    const isNewDetail = (detail) => {
        return !contactHeaders.includes(detail)
    }

    const removeDetail = detail => {
        const newDetailsForMapping = contactMetaForMapping.filter(item => item !== detail)
        setContactMetaForMapping(newDetailsForMapping)
    }

    function checkIfRoleExists(role) {
        if (rolesForMapping.includes(role)) {
            const prefix = "CUSTOM"
            const newIndex = parseInt(role.substring(prefix.length), 10) + 1
            const newRole = prefix + newIndex
            return checkIfRoleExists(newRole)
        } else {
            return role
        }
    }
    const addCustomRole = () => {
        const customRoles = rolesForMapping.filter(role => role.startsWith("CUSTOM"))
        const newRoleIndex = customRoles?.length + 1
        const newRole = checkIfRoleExists("CUSTOM" + newRoleIndex)
        const newRolesForMapping = [...rolesForMapping]
        newRolesForMapping.push(newRole)
        setRolesForMapping(newRolesForMapping)
    }
    const isNewRole = (role) => {
        return !supportRolesData.includes(role)
    }
    const removeRole = role => {
        const newRolesForMapping = rolesForMapping.filter(item => item !== role)
        setRolesForMapping(newRolesForMapping)
    }

    const isValueMissingFromContacts = key => {
        return contacts.some(obj => {
            if (obj.data && obj.data.hasOwnProperty(key)) {
                const value = obj.data[key]
                return value === null || value === undefined || value === ''
            }
            return true // If the object doesn't have a 'data' property or the key is not present
        })
    }

    const findMissingValuePositions = (key) => {
        const missingIndices = []

        contacts.forEach((obj, index) => {
            if (obj.data && obj.data.hasOwnProperty(key)) {
                const value = obj.data[key]
                if (value === null || value === undefined || value === '') {
                    missingIndices.push(index)
                }
            } else {
                missingIndices.push(index)
            }
        })

        return missingIndices
    }

    const findMissingIds = (key) => {
        const missingIds = []
        const userIdsToTest = extractValuesByKey(contacts, key)

        // Build a set of all the IDs and aliases from userData
        const idsAndAliases = new Set()
        userData.forEach(user => {
            idsAndAliases.add(user.id.toLowerCase())
            if (user?.meta?.userid) {
                idsAndAliases.add(user.meta.userid)
            }
            if (user?.meta?.aliases) {
                const aliases = user.meta.aliases
                aliases.forEach(alias => idsAndAliases.add(alias.trim().toLowerCase()))
            }
        })

        // Test each userId against the set of IDs and aliases
        userIdsToTest.forEach(userId => {
            if (!idsAndAliases.has(userId.toLowerCase())) {
                missingIds.push(userId)
            }
        })
        return missingIds
    }

    return <div className={"define-roles"}>
        <div className={"role-header"}>
            <h4>{intl.formatMessage({id:"defineColumns", defaultMessage:"Match file\'s columns to student information"})}</h4>
        </div>
        <div className={"role-editing"}>
            <div className={"role-row"}>
                <h5>{intl.formatMessage({id:'studentInformation', defaultMessage:'Student information'})}</h5>
                <h5>{intl.formatMessage({id:'xlsColumn', defaultMessage:'Excel column'})}</h5>
                <h5>{intl.formatMessage({id:'exampleFromData', defaultMessage:'Example from data'})}</h5>
            </div>
            {sortedColumnHeaders.map((meta,i) => {
                const activeValue = metaColumnNames[meta]?.column ?? ""
                const valueMissing = activeValue ? isValueMissingFromContacts(activeValue) : false
                return <ContactMetaMapper key={meta}
                                          selectValue={meta === "phonenumber" ? "phonenumber" : activeValue}
                                          meta={meta}
                                          data={data}
                                          newColumn={isNewDetail(meta)}
                                          metaColumnNames={metaColumnNames}
                                          setMetaColumnNames={setMetaColumnNames}
                                          removeColumn={column=>removeDetail(column)}
                                          required={contactMeta.hasOwnProperty(meta) && contactMeta[meta]?.required}
                                          contactMissingValue={valueMissing}
                                          missingValuePositions={valueMissing ? findMissingValuePositions(activeValue) : []}
                                          countryCode={countryCode}
                                          contacts={contacts}
                                          errorCheckObject={errorCheckObject}
                                          setErrorCheckObject={setErrorCheckObject}
                />
            })}
            <div className={"role-row"}>
                <button onClick={()=>addCustomDetail()}>
                    {intl.formatMessage({id:"addDetail", defaultMessage:"+ Create new detail"})}
                </button>
            </div>
            <div className={"role-row"}>
                <h5>{intl.formatMessage({id:'mapColumnsToRoles', defaultMessage:'Map columns to roles'})}</h5>
                <h5>{intl.formatMessage({id:'xlsColumn', defaultMessage:'Excel column'})}</h5>
                <h5>{intl.formatMessage({id:'exampleFromData', defaultMessage:'Example from data'})}</h5>
            </div>

            {rolesForMapping.map((role,i) => {
                const activeValue = Object.keys(data?.extracolumn).find(key => data?.extracolumn[key] === role)
                const valueMissing = isValueMissingFromContacts(activeValue)
                // Check for undefined and not display missing values if thats the case
                const missingIds = activeValue === undefined ? [] : findMissingIds(activeValue)
                const invalidIds = missingIds?.length > 0

                return <RoleMapper key={role}
                                   role={role}
                                   data={data}
                                   setValidateData={setValidateData}
                                   newRole={isNewRole(role)}
                                   removeRole={role=>removeRole(role)}
                                   roleNames={roleNames}
                                   setRoleNames={setRoleNames}
                                   metaColumnNames={metaColumnNames}
                                   setMetaColumnNames={setMetaColumnNames}
                                   activeColumn={activeValue}
                                   contactMissingValue={valueMissing}
                                   missingValuePositions={valueMissing ? findMissingValuePositions(activeValue) : []}
                                   invalidLoginIds={invalidIds}
                                   missingIds={missingIds}
                                   errorCheckObject={errorCheckObject}
                                   setErrorCheckObject={setErrorCheckObject}
                                   roleOptions={rolesForMapping}
                                   userMeta={userMeta}
                                   refetchUsers={refetchUsers}
                                   userData={userData}
                />
            })}
            <div className={"role-row"}>
                <button onClick={()=>addCustomRole()}>
                    {intl.formatMessage({id:"addRole", defaultMessage:"+ Add role"})}
                </button>
            </div>
        </div>
    </div>
}

function ContactMetaMapper({errorCheckObject, setErrorCheckObject, countryCode, contacts, contactMissingValue, missingValuePositions, selectValue, meta, data, newColumn, metaColumnNames, setMetaColumnNames, removeColumn, required}) {
    const intl = useIntl()
    const columns = data?.extracolumn
    const {instanceLang} = useLanguageData()
    const {instanceLangOptions} = useLanguageOptionsData()
    const multipleInstanceLangs = instanceLangOptions?.length > 1
    const extraPadding = newColumn && multipleInstanceLangs
    const isPhonenumber = meta === "phonenumber"
    const hasErrors = data.hasOwnProperty("errors") && data.errors.length > 0
    const displaySuccessData = (!isPhonenumber && !contactMissingValue) || (isPhonenumber && !hasErrors)
    //const [displaySuccessData, setDisplaySuccessData] = useState(true)

    useEffect(()=>{
        const checksFailed = (required && contactMissingValue) || (isPhonenumber && hasErrors)
        setErrorCheckObject((prevErrorCheckObject) => ({
            ...prevErrorCheckObject,
            [meta]: !checksFailed
        }))
    },[selectValue])

    const setMetaToColumn = (meta, value) => {
        let newColumnNames = {...metaColumnNames}
        newColumnNames[meta]['column'] = value
        setMetaColumnNames(newColumnNames)
    }

    const editColumnName = (name, lang) => {
        let newColumnNames = {...metaColumnNames}
        newColumnNames[meta][lang] = name
        setMetaColumnNames(newColumnNames)
    }

    const isColumnTakenInInfo = value => {
        for (let key in metaColumnNames) {
            if (metaColumnNames[key]?.column === value) {
                return true
            }
        }
        return false
    }

    const isColumnTakenInRoles = column => {
        return columns[column]
    }

    const isColumnTaken = value => isColumnTakenInInfo(value) || isColumnTakenInRoles(value)

    function groupErrorsByError(data) {
        let errors = {}

        data.forEach(item => {
            let errorCamelCase = item.error.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
                if (+match === 0) return ''
                return index === 0 ? match.toLowerCase() : match.toUpperCase()
            })

            if (!errors[errorCamelCase]) {
                errors[errorCamelCase] = []
            }

            if (errorCamelCase === 'noPhonenumber') {
                // Because no phonenumber to display, lets show the user something
                let firstTwoKeysWithValues = Object.keys(item.data)
                    .filter(key => item.data[key])
                    .slice(0, 2)
                    .reduce((obj, key) => {
                        obj[key] = item.data[key]
                        return obj
                    }, {})
                    // Push null if nothing is found
                if (Object.keys(firstTwoKeysWithValues).length === 0) {
                    errors[errorCamelCase].push(null)
                } else {
                    let formattedEntries = Object.entries(firstTwoKeysWithValues)
                        .map(([key, value]) => `${key}: ${value}`)
                        .join(", ")
                    errors[errorCamelCase].push(formattedEntries)
                }
            } else {
                errors[errorCamelCase].push(item.data?.phonenumber)
            }
        })

        return errors
    }

    const isPhoneNumberInvalid = (e, countryCode = "") => {
        if (!e || typeof e !== 'object' || !e?.data || !e.data?.phonenumber) {
            return false
        }

        const phoneNumber = e.data.phonenumber
        let zeroAfterCountryCode = false

        if (countryCode && phoneNumber.startsWith(countryCode)) {
            const numberWithoutCountryCode = phoneNumber.slice(countryCode.length)
            zeroAfterCountryCode = numberWithoutCountryCode.startsWith("0")
        }

        const phoneNumberLength = phoneNumber.length

        const minLength = 5
        const maxLength = 15
        const invalidLength = phoneNumberLength < minLength || phoneNumberLength > maxLength

        const hasInvalidCharacters = /[^+0-9]/g.test(phoneNumber)

        const multiplePlusSigns = (phoneNumber.match(/\+/g) || []).length > 1

        return zeroAfterCountryCode || invalidLength || hasInvalidCharacters || multiplePlusSigns
    }

    const findInvalidPhoneNumbers = (dataArray, countryCode = "") => {
        if (!Array.isArray(dataArray)) {
            return []
        }

        return dataArray
            .filter(item => isPhoneNumberInvalid(item, countryCode))
            .map(item => item?.data?.phonenumber)
    }

    const findNumbersWithoutCountryCode = (dataArray, countryCode) => {
        if (!Array.isArray(dataArray) || !countryCode) {
            return []
        }

        return dataArray
            .filter(item => item?.data?.phonenumber &&
                !item.data.phonenumber.startsWith(countryCode) &&
            !isPhoneNumberInvalid(item, countryCode))
            .map(item => item.data.phonenumber)
    }

    const renderPhoneNumberAlerts = () => {
        if (!isPhonenumber) {
            return null
        } else {
            const invalidPhoneNumbers = findInvalidPhoneNumbers(contacts, countryCode)
            const phonenumberFormAlert = invalidPhoneNumbers?.length > 0 ?
                <div className={"test-result info"}>
                    <SnackInfo />
                <FormattedMessage
                    id={'invalidPhoneNumbers.info'}
                    defaultMessage={'Check the following numbers: {phonenumbers}'}
                    values={{phonenumbers: <FormatPhoneNumberDisplay phonenumberArray={invalidPhoneNumbers} intl={intl} />}}
                /></div> : null

            const phoneNumbersWithoutCountryCode = findNumbersWithoutCountryCode(contacts, countryCode)
            const foreignPhonenumbers = phoneNumbersWithoutCountryCode?.length > 0 ?
                <div className={"test-result info"}>
                    <SnackInfo />
                    <FormattedMessage
                        id={'foreignPhonenumbers.info'}
                        defaultMessage={'International phone numbers found: {phonenumbers}'}
                        values={{phonenumbers: <FormatPhoneNumberDisplay phonenumberArray={phoneNumbersWithoutCountryCode} intl={intl} />}}
                    /></div> : null

            return <>{phonenumberFormAlert}{foreignPhonenumbers}</>
        }
    }

    const renderTestResult = () => {
        if (isPhonenumber) {
            if (hasErrors) {
                const errorObject = groupErrorsByError(data?.errors)

                return Object.keys(errorObject).map((error,i) => {
                    return <div className={"test-result fail"} key={i}>
                        <SnackError />
                        <FormattedMessage
                            id={error+'.fail'}
                            defaultMessage={error}
                            values={{phonenumbers: <FormatPhoneNumberDisplay phonenumberArray={errorObject[error]} intl={intl} />}}
                        />
                    </div>
                })
            }
            if (contactMissingValue) {
                return <div className={"test-result fail"}>
                    <SnackError />
                    <FormattedMessage
                        id={'missingDataInColumn.fail'}
                        defaultMessage={'Row(s) {rows} are missing data in the assigned column. Please review and re-upload.'}
                        values={{rows: formatMissingRows(missingValuePositions)}}
                    />
                </div>
            }
        } else {
            if (contactMissingValue) {
                if (required) {
                    return <div className={"test-result fail"}>
                        <SnackError />
                        <FormattedMessage
                            id={'missingDataInColumn.fail'}
                            defaultMessage={'Row(s) {rows} are missing data in the assigned column. Please review and re-upload.'}
                            values={{rows: formatMissingRows(missingValuePositions)}}
                        />
                    </div>
                } else {
                    return <div className={"test-result info"}>
                        <SnackInfo />
                        <FormattedMessage
                            id={'missingDataInColumn.info'}
                            defaultMessage={'Row(s) {rows} are missing data in the assigned column.'}
                            values={{rows: formatMissingRows(missingValuePositions)}}
                        />
                    </div>
                }
            }
        }
        return <div className={"test-result success"}>
            <HoverTooltip text={intl.formatMessage({id:'missingDataInColumn.success', defaultMessage:'No data missing from rows.'})}>
                <SnackSuccess />
            </HoverTooltip>
            {data?.contacts.slice(0, calculateSlice(data?.contacts, selectValue, 50)).map((item, i, slicedArray) => {
                const valueExp = item?.data[selectValue]
                const isLastItem = i === slicedArray.length - 1;
                const shouldRenderEllipsis = data?.contacts.length > calculateSlice(data?.contacts, selectValue, 50);
                const appendComma = !isLastItem || (isLastItem && shouldRenderEllipsis)

                if (valueExp === null || valueExp === undefined || valueExp === '') {
                    return <span key={i}>
                                    {intl.formatMessage({defaultMessage:'(Empty)', id:'survey.table.empty'})}
                        {appendComma && ', '}
                                </span>
                } else {
                    return <span key={i}>
                                    {valueExp.toString()}
                        {appendComma && ', '}
                    </span>
                }
            })}
            {data?.contacts.length > calculateSlice(data?.contacts, selectValue, 50) && '...'}
        </div>
    }

    const selectClass = () => {
        let classNames = ''
        if (!selectValue) {
            classNames += ' unset'
        } else {
            if (contactMissingValue) {
                if (required) {
                    classNames += ' fail'
                } else {
                    classNames += ' info'
                }
            } else {
                classNames += ' success'
            }
        }
        return classNames
    }

    return <div className={extraPadding ? "role-row extra-padding" : "role-row"}>
        {newColumn ?
            multipleInstanceLangs ?
                <div className={"multi-inputs"}>
                    <div>
                        {instanceLangOptions.map((lang, i) => {
                            return <div key={i}>
                                <label htmlFor={"metaTranslation-"+lang}>
                                    {intl.formatMessage({
                                        id: lang,
                                        defaultMessage: lang
                                    })}
                                </label>
                                <input name={"metaTranslation-"+lang} id={"metaTranslation-"+lang} placeholder={intl.formatMessage({id:"typeLabelName", defaultMessage:"Type label name"})} autoFocus={i===0} value={metaColumnNames[meta][lang]} onChange={e=>editColumnName(e.target.value, lang)}/></div>
                        })}
                    </div>
                </div>
                : <p><input placeholder={intl.formatMessage({id:"typeLabelName", defaultMessage:"Type label name"})} autoFocus value={metaColumnNames[meta][instanceLang]} onChange={e=>editColumnName(e.target.value, instanceLang)}/></p> :
            required ?
                <HoverTooltip text={intl.formatMessage({id:'required', defaultMessage:'Required'})}>
                    <p className={"required"}>
                        {intl.formatMessage({
                            id: `contactmeta.${meta}`,
                            defaultMessage: meta
                        })}*
                    </p>
                </HoverTooltip> :
            <p>
                {intl.formatMessage({
                    id: `contactmeta.${meta}`,
                    defaultMessage: meta
                })}
            </p>
        }
        {isPhonenumber ?
        <select value={"phonenumber"} disabled className={selectClass()}>
            <option value={"phonenumber"}>phonenumber</option>
        </select> :
            <select value={selectValue} className={selectClass()} onChange={e => setMetaToColumn(meta, e.target.value)}>
                <option value={""}>– {intl.formatMessage({id:"chooseColumn", defaultMessage:"Choose column"})} –</option>
                {Object.keys(columns).map((column, i)=>{
                    return <option key={i} disabled={isColumnTaken(column)} value={column}>{column}</option>
                })}
            </select>
        }
        {newColumn &&
            <div className={"remove-row"}>
                <button onClick={()=>removeColumn(meta)}><CloseIcon /></button>
                {selectValue &&
                    <div className={"example"}>
                        {renderTestResult()}
                        {renderPhoneNumberAlerts()}
                    </div>
                }
            </div>
        }
        {selectValue && !newColumn &&
            <div className={"example"}>
                {renderTestResult()}
                {renderPhoneNumberAlerts()}
            </div>
        }
    </div>
}

function RoleMapper({role, userMeta, refetchUsers, roleOptions, data, activeColumn, errorCheckObject, setErrorCheckObject, contactMissingValue, invalidLoginIds, missingIds, missingValuePositions, setValidateData, newRole, removeRole, roleNames, setRoleNames, metaColumnNames, setMetaColumnNames, userData}) {
    const intl = useIntl()
    const columns = data?.extracolumn
    const extraColumns = Object.keys(columns)
    const displaySuccessData = !invalidLoginIds && !contactMissingValue
    const getKeyByValue = (object, value) => {
        return Object.keys(object).find(key => object[key] === value)
    }

    useEffect(()=>{
        const checksFailed = invalidLoginIds
        setErrorCheckObject((prevErrorCheckObject) => ({
            ...prevErrorCheckObject,
            [role]: !checksFailed
        }))
    },[activeColumn, userData])

    const isColumnTakenInInfo = value => {
        for (let key in metaColumnNames) {
            if (metaColumnNames[key]?.column === value) {
                return true
            }
        }
        return false
    }

    const isColumnTakenInRoles = column => {
        return columns[column]
    }

    const isColumnTaken = value => isColumnTakenInInfo(value) || isColumnTakenInRoles(value)

    const setRoleToColumn = (role, column) => {
        const newData = {...data}
        const previousKey = getKeyByValue(columns, role)
        if (previousKey) {
            // remove earlier column selection
            newData.extracolumn[previousKey] = null
        }
        if (column) {
            newData.extracolumn[column] = role
        }
        setValidateData(newData)
    }

    const {instanceLang} = useLanguageData()
    const {instanceLangOptions} = useLanguageOptionsData()
    const multipleInstanceLangs = instanceLangOptions?.length > 1

    const editRoleName = (name, lang) => {
        let newRoleNames = {...roleNames}
        newRoleNames[role][lang] = name
        setRoleNames(newRoleNames)
    }

    const extraPadding = newRole && multipleInstanceLangs

    const headerName = (column) => {
        return intl.formatMessage({
            id: column,
            defaultMessage: column
        })
    }

    const userHeaders = Object.keys(userMeta).sort((a, b) => {
        const orderA = userMeta[a]?.order ?? Infinity
        const orderB = userMeta[b]?.order ?? Infinity
        return orderA - orderB
    })

    const renderMissingUsers = () => {
        if (invalidLoginIds) {
            return <div className={"test-result fail"}>
                <SnackError />
                <FormattedMessage
                    id={'unrecognizedLoginIds.fail'}
                    defaultMessage={'Unrecognized login IDs found: {ids}'}
                    values={{ids: <FormatPhoneNumberDisplay phonenumberArray={missingIds} intl={intl}/>}}
                />
                <AddUser options={userHeaders} headerName={headerName} refetchUsers={refetchUsers} userMeta={userMeta} roleOptions={roleOptions}/>
            </div>
        }
        return null
    }

    const renderTestResult = () => {
            if (contactMissingValue) {
                return <div className={"test-result info"}>
                    <SnackInfo />
                    <FormattedMessage
                        id={'missingDataInColumn.info'}
                        defaultMessage={'Row(s) {rows} are missing data in the assigned column.'}
                        values={{rows: formatMissingRows(missingValuePositions)}}
                    />
                </div>
        }
        return null
    }

    const selectClass = () => {
        let classNames = ''
        if (!activeColumn) {
            classNames += ' unset'
        } else {
            if (invalidLoginIds) {
                classNames += ' fail'
            } else if (contactMissingValue) {
                classNames += ' info'
            } else {
                classNames += ' success'
            }
        }
        return classNames
    }

    const renderErrorDisplay = () => (
        <div className={"example"}>
            {renderMissingUsers()}
            {renderTestResult()}
            {displaySuccessData &&
                <div className={"test-result success"}>
                    <HoverTooltip text={intl.formatMessage({id:'missingDataInColumn.success', defaultMessage:'No data missing from rows.'})}>
                        <SnackSuccess />
                    </HoverTooltip>
                    {data?.contacts.slice(0, calculateSlice(data?.contacts, activeColumn, 50)).map((item, i, slicedArray) => {
                        const valueExp = item?.data[activeColumn]
                        const isLastItem = i === slicedArray.length - 1;
                        const shouldRenderEllipsis = data?.contacts.length > calculateSlice(data?.contacts, activeColumn, 50);
                        const appendComma = !isLastItem || (isLastItem && shouldRenderEllipsis)

                        if (valueExp === null || valueExp === undefined || valueExp === '') {
                            return <span key={i}>
                                    {intl.formatMessage({defaultMessage:'(Empty)', id:'survey.table.empty'})}
                                {appendComma && ', '}
                                </span>
                        } else {
                            return <span key={i}>
                                    {valueExp.toString()}
                                {appendComma && ', '}
                    </span>
                        }
                    })}
                    {data?.contacts.length > calculateSlice(data?.contacts, activeColumn, 50) && '...'}
                </div>
            }
        </div>
    )

    return <div className={extraPadding ? "role-row extra-padding" : "role-row"}>
        {newRole ?
            multipleInstanceLangs ?
                <div className={"multi-inputs"}>
                    <div>
                    {instanceLangOptions.map((lang, i) => {
                        return <div key={i}>
                            <label htmlFor={"roleTranslation-"+lang}>
                                {intl.formatMessage({
                                    id: lang,
                                    defaultMessage: lang
                                })}
                            </label>
                            <input name={"roleTranslation-"+lang} id={"roleTranslation-"+lang} placeholder={intl.formatMessage({id:"typeRoleName", defaultMessage:"Type role name"})} autoFocus={i===0} value={roleNames[role][lang]} onChange={e=>editRoleName(e.target.value, lang)}/></div>
                    })}
                    </div>
                </div>
             : <p><input placeholder={intl.formatMessage({id:"typeRoleName", defaultMessage:"Type role name"})} autoFocus value={roleNames[role][instanceLang]} onChange={e=>editRoleName(e.target.value, instanceLang)}/></p> :
            <p>
                {intl.formatMessage({
                    id: "role."+role,
                    defaultMessage: role
                })}
            </p>
        }
        <select value={activeColumn} className={selectClass()} onChange={e => setRoleToColumn(role, e.target.value)}>
            <option value={""}>– {intl.formatMessage({id:"chooseColumn", defaultMessage:"Choose column"})} –</option>
            {extraColumns.map((column, i)=>{
                return <option key={i} disabled={isColumnTaken(column)} value={column}>{column}</option>
            })}
        </select>
        {newRole &&
        <div className={"remove-row"}>
            <div className={"flex"}>
                <button onClick={()=>removeRole(role)}><CloseIcon /></button>
                {activeColumn && data?.contacts?.length > 0 &&
                    renderErrorDisplay()
                }
            </div>
        </div>
        }
        {!newRole && activeColumn && data?.contacts?.length > 0 &&
            renderErrorDisplay()
        }
    </div>
}

function FileErrorCheck({data, countryCode, rolesNotDefinedOrIdsAreUsers, aRoleIsDefined, usersInRolesThatAreNotAnnieUsers}) {
    const allContacts = data.contacts.concat(data.errors)
    const noErrors = data?.errors.length < 1
    const noFirstname = !noErrors && data.errors.some(e => e.error.includes("no firstname"))
    const noLastname = !noErrors && data.errors.some(e => e.error.includes("no lastname"))
    const noPhonenumber = !noErrors && data.errors.some(e => e.error.includes("no phonenumber"))
    const duplicates = !noErrors && data.errors.some(e => e.error.includes("duplicate phone number"))
    const noTeacheruid = !noErrors && data.errors.some(e => e.error.includes("no teacheruid"))
    const teacheruidNotFound = !noErrors && data.errors.some(e => e.error.includes("teacheruid refers to an unknown"))
    const intl = useIntl()

    const noPhonenumberText = noPhonenumber ? intl.formatMessage(
            {
                id: 'contact.upload.errorCheck.missingPhonenumbers',
                defaultMessage: 'Missing phonenumber(s)',
            }) : ""
    const duplicatesText = duplicates ? intl.formatMessage(
        {
            id: 'contact.upload.errorCheck.duplicatePhonenumbers',
            defaultMessage: 'Duplicate phonenumber(s)',
        }) : ""
    const noTeacheruidText = noTeacheruid ? intl.formatMessage(
        {
            id: 'contact.upload.errorCheck.missingTeacheruids',
            defaultMessage: 'Missing teacheruid(s)',
        }) : ""
    const teacheruidNotFoundText = teacheruidNotFound ? intl.formatMessage(
        {
            id: 'contact.upload.errorCheck.teacheruidsNotFound',
            defaultMessage: 'Used teacheruid(s) is not user',
        }) : ""

    const contactsWithDifferentCountryCode = allContacts.filter(e => e.data.hasOwnProperty("phonenumber") && e.data["phonenumber"] && !e.data["phonenumber"].startsWith(countryCode))

    const numberIsInvalid = e => {
        const phoneNumber = e.data.hasOwnProperty("phonenumber") && e.data["phonenumber"]
        if (!phoneNumber) {
            return false
        }

        let zeroAfterCountryCode

        if (phoneNumber.startsWith(countryCode)) {
            const numberWithoutCountryCode = phoneNumber.substring(countryCode.length)
            zeroAfterCountryCode = numberWithoutCountryCode.startsWith("0")
        }

        const phoneNumberLength = phoneNumber.length
        const invalidLength = phoneNumberLength < 5 || phoneNumberLength > 15

        const charsOutsideValid = /[^+,0-9]/g.test(phoneNumber)

        const multiplePlusSigns = phoneNumber.match(/[+]/g) && phoneNumber.match(/[+]/g).length > 1

        return zeroAfterCountryCode || invalidLength || charsOutsideValid || multiplePlusSigns
    }

    const numbersFailedValidation = allContacts.some(e => numberIsInvalid(e))

    const contactsThatFailedNumberValidation = allContacts.filter(e => numberIsInvalid(e))

    const usersInRolesWithoutNull = usersInRolesThatAreNotAnnieUsers && usersInRolesThatAreNotAnnieUsers.filter(user => user !== null)
    const missingUserInfo = usersInRolesThatAreNotAnnieUsers && usersInRolesThatAreNotAnnieUsers.includes(null)

    return <div className={"scroll-content"}>
        <div className={"survey-summary-container"}>
            <Slat header={intl.formatMessage(
                {
                    id: 'contact.upload.errorCheck.header.duplicatePhonenumbers',
                    defaultMessage: 'Duplicate phonenumbers',
                })}
                  text={!duplicates && intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.text.duplicatePhonenumbers',
                          defaultMessage: 'No duplicates',
                      })}
                  success={!duplicates}
                  errorText={duplicatesText}
                  expandable={duplicates}
            >
                {duplicates &&
                <DataTable data={data} error={"duplicate phone number"} />
                }
            </Slat>
            <Slat header={intl.formatMessage(
                {
                    id: 'contact.upload.errorCheck.header.missingPhonenumbers',
                    defaultMessage: 'Phonenumbers',
                })}
                  text={!noPhonenumber && intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.text.missingPhonenumbers',
                          defaultMessage: 'No missing phonenumbers',
                      })}
                  success={!noPhonenumber}
                  errorText={noPhonenumberText}
                  expandable={noPhonenumber}
            >
                {noPhonenumber &&
                <DataTable data={data} error={"no phonenumber"} />
                }
            </Slat>
            <Slat header={intl.formatMessage(
                {
                    id: 'contact.upload.errorCheck.header.foreignNumbers',
                    defaultMessage: 'Foreign numbers',
                })}
                  success={true}
                  text={contactsWithDifferentCountryCode.length < 1 && intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.text.foreignNumbers',
                          defaultMessage: 'No foreign phonenumbers',
                      })}
                  info={contactsWithDifferentCountryCode.length > 0}
                  infoText={intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.foreignNumbers',
                          defaultMessage: 'Foreign number(s)',
                      })}
                  expandable={contactsWithDifferentCountryCode.length > 0}
            >
                {contactsWithDifferentCountryCode.length > 0 &&
                    <DataTable data={data} error={false} dataToDisplay={contactsWithDifferentCountryCode} />
                }
            </Slat>
            <Slat header={intl.formatMessage(
                {
                    id: 'contact.upload.errorCheck.header.validNumbers',
                    defaultMessage: 'Valid numbers',
                })}
                success={true}
                text={!numbersFailedValidation && intl.formatMessage(
                {
                    id: 'contact.upload.errorCheck.text.validNumbers',
                    defaultMessage: 'Valid numbers',
                })}
                  info={numbersFailedValidation}
                  infoText={intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.validNumbers',
                          defaultMessage: 'Some numbers failed validation. Please check numbers.',
                      })}
                  expandable={numbersFailedValidation}
            >
                {numbersFailedValidation &&
                <DataTable data={data} error={false} dataToDisplay={contactsThatFailedNumberValidation} />
                }
            </Slat>
            <Slat header={intl.formatMessage(
                {
                    id: 'contact.upload.errorCheck.header.firstname',
                    defaultMessage: 'Firstname',
                })}
                  text={!noFirstname ? intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.text.firstname',
                          defaultMessage: 'No missing fistnames',
                      }) : false}
                  success={!noFirstname}
                  errorText={intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.firstname',
                          defaultMessage: 'Missing firstname(s)',
                      })}
                  expandable={noFirstname}
            >
                {noFirstname &&
                <DataTable data={data} error={"no firstname"} />
                }
            </Slat>
            <Slat header={intl.formatMessage(
                {
                    id: 'contact.upload.errorCheck.header.lastname',
                    defaultMessage: 'Lastname',
                })}
                  text={!noLastname ? intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.text.lastname',
                          defaultMessage: 'No missing lastnames',
                      }) : false}
                  success={!noLastname}
                  errorText={intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.lastname',
                          defaultMessage: 'Missing lastname(s)',
                      })}
                  expandable={noLastname}
            >
                {noLastname &&
                <DataTable data={data} error={"no lastname"} />
                }
            </Slat>
            <Slat header={intl.formatMessage(
                {
                    id: 'contact.upload.errorCheck.header.missingTeacheruids',
                    defaultMessage: 'Missing teacheruids',
                })}
                  text={!noTeacheruid && intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.text.missingTeacheruids',
                          defaultMessage: 'No missing teacheruids',
                      })}
                  success={!noTeacheruid}
                  errorText={noTeacheruidText}
                  expandable={noTeacheruid}
            >
                {noTeacheruid &&
                <DataTable data={data} error={"no teacheruid"} />
                }
            </Slat>
            <Slat header={intl.formatMessage(
                {
                    id: 'contact.upload.errorCheck.header.teacheruidsNotFound',
                    defaultMessage: 'Users for teachers',
                })}
                  text={!teacheruidNotFound && intl.formatMessage(
                      {
                          id: 'contact.upload.errorCheck.text.teacheruidsNotFound',
                          defaultMessage: 'Given teachers are users',
                      })}
                  success={!teacheruidNotFound}
                  errorText={teacheruidNotFoundText}
                  expandable={teacheruidNotFound}
            >
                {teacheruidNotFound &&
                <DataTable data={data} error={"teacheruid refers to an unknown"} />
                }
            </Slat>
            <Slat header={intl.formatMessage(
                {
                    id: 'header.usersForRoles',
                    defaultMessage: 'User ids for roles',
                })}
                  text={!rolesNotDefinedOrIdsAreUsers ?
                      intl.formatMessage(
                          {
                              id: 'overview.clickToViewUndefinedUserIds',
                              defaultMessage: 'Click to view missing Annie users',
                          }) :
                  aRoleIsDefined ?
                      intl.formatMessage(
                          {
                              id: 'successText.usersForRoles',
                              defaultMessage: 'Given role ids are users',
                          }) :
                      intl.formatMessage(
                          {
                              id: 'successText.noRolesDefined',
                              defaultMessage: 'No roles defined',
                          })}
                  success={rolesNotDefinedOrIdsAreUsers}
                  errorText={intl.formatMessage(
                      {
                          id: 'errorText.usersForRoles',
                          defaultMessage: 'Some or all given role ids are not users',
                      })}
                  expandable={!rolesNotDefinedOrIdsAreUsers}
            >
                {!rolesNotDefinedOrIdsAreUsers &&
                    <div className={"info-container"}>
                        <ul>
                        {usersInRolesWithoutNull?.length > 0 &&
                            <>
                                <li><b>{intl.formatMessage({
                                    id: "roleIdsAreNotAnnieUsers",
                                    defaultMessage: "Given ids for roles are not Annie users:"
                                })}</b></li>
                                <ul>
                                    {usersInRolesWithoutNull.map((user, i)=> (
                                        <li key={i}>{user}</li>
                                    ))}
                                </ul>
                            </>
                        }
                        {missingUserInfo &&
                        <li>
                            <b>
                                {intl.formatMessage({id: "contactsMissingProviderInfo", defaultMessage:"Students missing support provider information"})}
                            </b>
                        </li>
                        }
                        </ul>
                    </div>
                }
            </Slat>
        </div>
    </div>
}

export function DataTable({data, error, dataToDisplay, userTable}) {
    const headers = error ? Object.keys(data.errors[0].data) : userTable ? Object.keys(data.errors.concat(data.annieusers)[0].data) : Object.keys(data.errors.concat(data.contacts)[0].data)
    const errorCells = userTable ? ["userid", "email", "firstname", "lastname"] : ["phonenumber", "firstname", "lastname", "teacheruid"]
    const duplicatePhonenumber = error === "duplicate phone number"
    const isData = dataToDisplay && dataToDisplay.length > 0
    const intl = useIntl()

    return <div className={"table-container"}>
        <table className={"summary-container"}>
        <thead>
        <tr>
            {headers.map((e,i) => {
                return <th key={i}>{e}</th>
            })}
        </tr>
        </thead>
        <tbody>
        {error &&
        data.errors.map((e,i) => {
            return e.error.includes(error) && <tr key={i}>
                {headers.map((key,i) => {
                    return <td key={i}>{e.data[key] ? error.includes(key) || (key === "phonenumber" && duplicatePhonenumber) ? <em className={"red"}>{e.data[key]}</em> : e.data[key] : <em className={errorCells.includes(key) ? "red" : ""}>{intl.formatMessage(
                        {
                            id: 'contact.upload.missingData',
                            defaultMessage: 'Missing data',
                        })}</em>}</td>
                })}
            </tr>
        })
        }
        {isData &&
        dataToDisplay.map((e, i) => {
          return <tr key={i}>
              {headers.map((key, i) => {
                  return <td key={i}>{e.data[key] ? key === "phonenumber" ? <em className={"blue"}>{e.data[key]}</em> : e.data[key] : <em className={errorCells.includes(key) ? "red" : ""}>{intl.formatMessage(
                      {
                          id: 'contact.upload.missingData',
                          defaultMessage: 'Missing data',
                      })}</em>}</td>
              })}
          </tr>
        })
        }
        </tbody>
    </table>
    </div>
}

function ContactsRender({ getContactsAndRoles, supportRolesData, refetchContacts, refetchingContacts, contactMeta, userData }) {
    const intl = useIntl()

    return <div className="contacts-table-container">
        {getContactsAndRoles.status === "loading" &&
            <h2>{intl.formatMessage(
                {
                    id: 'loading',
                    defaultMessage: 'Loading...',
                })}</h2>
        }
        {getContactsAndRoles.status === "error" &&
            <h2>{intl.formatMessage(
                {
                    id: 'contact.errorGettingContacts',
                    defaultMessage: 'Error in getting contacts.',
                })}</h2>
        }
        {getContactsAndRoles.status === "success" &&
            <ContactsTable contacts={getContactsAndRoles?.data?.contacts} contactMeta={contactMeta} supportRolesData={supportRolesData} refetchContacts={refetchContacts} refetchingContacts={refetchingContacts} userData={userData} surveyData={null} setSurveyData={()=>console.log("How did you get here?")} />
        }
    </div>
}

export function ContactsTable({ contacts, supportRolesData, refetchContacts, refetchingContacts, contactMeta, surveyRecipients, surveyData, setSurveyData, userData, userMeta, refetchUsers }) {
    const intl = useIntl()
    const contactMetaColumns = Object.keys(contactMeta).sort((a, b) => {
        const orderA = contactMeta[a]?.order ?? Infinity
        const orderB = contactMeta[b]?.order ?? Infinity
        return orderA - orderB
    })

    const contactsData = contacts.map(contact => ({
        id: contact?.id,
        ...contact
    }))

    const contactColumns = contactMetaColumns.map( meta => {
        const useDropdown = contactMeta[meta]?.type === 'enum'
        const date = contactMeta[meta]?.type === 'date'
        if (useDropdown) {
            return {
                Header: intl.formatMessage({
                    id: `contactmeta.${meta}`,
                    defaultMessage: meta
                }),
                accessor: "meta."+meta,
                Filter: MultiSelectColumnFilter,
                filter: filterIncludes,
                Cell: ({ value }) => {
                    if (value === null || value === undefined) {
                        return ''
                    }
                    return String(value)
                }
            }
        }
        if (date) {
            return {
                Header: intl.formatMessage({
                    id: `contactmeta.${meta}`,
                    defaultMessage: meta
                }),
                accessor: "meta."+meta,
                Cell: ({cell: {value}}) => (
                    <>{value && new Date(value).toLocaleString([intl.locale], {year:'numeric',month:'numeric',day:'numeric',hour: '2-digit', minute:'2-digit'})}</>
                )
            }
        }
        return {
            Header: intl.formatMessage({
                id: `contactmeta.${meta}`,
                defaultMessage: meta
            }),
            accessor: "meta."+meta,
            Cell: ({ value }) => {
                if (value === null || value === undefined) {
                    return ''
                }
                return String(value)
            }
        }
    })

    contactColumns.push({
        Header: '',
        accessor: 'divider',
        width: 0,
        maxWidth: 0,
        Cell: () => (
            <span />
        )
    })

    const roleColumns = supportRolesData.map( role => {
        return {
            Header: intl.formatMessage({
                id: "role."+role,
                defaultMessage: role
            }),
            accessor: "roles."+role,
            Cell: ({value}) => {
                if (value === null || value === undefined) {
                    return ''
                }
                const users = value.map(user => findUserById(user, userData))

                return users.map((user, i) => (
                    <React.Fragment key={i}>
                        {user}{i < users.length - 1 ? ', ' : ''}
                        {i < users.length - 1 && <br />}
                    </React.Fragment>
                ))
            },
            filter: (rows, id, filterValue) => {
                return rows.filter(row => {
                    const cellValue = row.values[id]
                    if (cellValue === null || cellValue === undefined) {
                        return false
                    }
                    const users = cellValue.map(userId => findUserById(userId, userData))

                    return users.some(user => user.toLowerCase().includes(filterValue.toLowerCase()))
                })
            },
            sortType: (rowA, rowB, columnId) => {
                const usersA = rowA.values[columnId]
                const usersB = rowB.values[columnId]

                if (usersA === null || usersA === undefined) {
                    return usersB === null || usersB === undefined ? 0 : 1
                }

                if (usersB === null || usersB === undefined) {
                    return -1
                }

                const usersNamesA = usersA.map(userId => findUserById(userId, userData)).join(", ")
                const usersNamesB = usersB.map(userId => findUserById(userId, userData)).join(", ")

                return usersNamesA.localeCompare(usersNamesB)
            },
        }
    })

    const columns = useMemo( () => [
        {
            Header: intl.formatMessage({defaultMessage:'Student Information', id:'studentInformation'}),
            columns: contactColumns,
        }, {
            Header: intl.formatMessage({defaultMessage:'Assigned Roles', id:'assignedRoles'}),
            columns: roleColumns,
        }
    ], [contactColumns, roleColumns])

    const data = useMemo(() => contactsData, [contactsData])

    function MultiSelectColumnFilter({column: { filterValue, setFilter, preFilteredRows, id }}) {
        const [showOptions, setShowOptions] = useState(false)
        const refOptions = useRef(null)
        const refOptionsToggle = useRef(null)
        useEffect(()=>{
            document.addEventListener("mousedown", handleClickOutside)
            function handleClickOutside(event) {
                if (refOptions.current && !refOptions.current.contains(event.target) && !refOptionsToggle.current.contains(event.target)) {
                    setShowOptions(false)
                }
                return () => {
                    document.removeEventListener("mousedown", handleClickOutside);
                }
            }
        },[refOptions])

        const options = React.useMemo(() => {
            const options = new Set()
            preFilteredRows.forEach(row => {
                const rowValue = String(row.values[id])
                if (!(rowValue === "null" || rowValue === "undefined")) {
                    options.add(rowValue)
                }
            })
            options.delete(null)
            options.delete(undefined)
            options.add("missing")
            return [...options.values()].sort()
        }, [id, preFilteredRows])

        const toggleFilter = option => {
            if (!filterValue) {
                setFilter([option])
            } else if (filterValue.includes(option)) {
                const index = filterValue.indexOf(option)
                if (index > -1) {
                    const newFilter = filterValue
                    newFilter.splice(index, 1)
                    setFilter(newFilter)
                }
            } else {
                const newFilter = filterValue
                newFilter.push(option)
                setFilter(newFilter)
            }
        }

        return <>
            <div className={"selected-filters-container"} onClick={e=>e.stopPropagation()}>
                <div ref={refOptionsToggle} className={"selected-filters"} onClick={()=>setShowOptions(!showOptions)}>
                    {filterValue ?
                        filterValue.length < 1 ? intl.formatMessage(
                            {
                                id: 'survey.table.showAll',
                                defaultMessage: 'Show All',
                            }) :
                            filterValue.map((filter, i) => {
                                if (filterValue.length === 1) {
                                    return filter
                                }
                                if (filterValue.length === i + 1) {
                                    return filter
                                }
                                return filter + ", "
                            }) : intl.formatMessage(
                            {
                                id: 'survey.table.showAll',
                                defaultMessage: 'Show All',
                            })
                    }
                </div>
                {showOptions &&
                <div ref={refOptions} className={"filter-options"}>
                    <div onClick={()=>setFilter("")}>
                        <input type={"checkbox"} readOnly={true} checked={filterValue === "" || filterValue === [] || !filterValue || filterValue.length < 1} />
                        {intl.formatMessage(
                            {
                                id: 'survey.table.showAll',
                                defaultMessage: 'Show All',
                            })}
                    </div>
                    {options.map((option, i) => (
                        <div key={i} onClick={()=>toggleFilter(option)}>
                            <input type={"checkbox"} readOnly={true} checked={filterValue !== undefined && filterValue && filterValue.includes(option)} />
                            {option === "missing" ?
                                intl.formatMessage(
                                    {
                                        id: 'survey.table.empty',
                                        defaultMessage: '(Empty)',
                                    })
                                : option}
                        </div>
                    ))}
                </div>
                }
            </div>
        </>
    }

    function filterIncludes(rows, id, filterValue) {
        return rows.filter(row => {
            const rowValue = row.values[id]

            if (filterValue.includes("missing") && (rowValue === null || rowValue === undefined || rowValue === '')) {
                return true
            }

            const normalizedValue = (rowValue === true) ? "true" : (rowValue === false) ? "false" : String(rowValue)

            if (!filterValue || filterValue.length < 1) {
                return true
            }

            return filterValue.includes(normalizedValue)
        })
    }

    return <div className={"contacts-table"}>
        <ContactTable columns={columns}
                      data={data}
                      contactMeta={contactMeta}
                      contactHeaders={contactMetaColumns}
                      supportRolesData={supportRolesData}
                      refetchContacts={refetchContacts}
                      refetchingContacts={refetchingContacts}
                      surveyRecipients={surveyRecipients}
                      surveyData={surveyData}
                      setSurveyData={setSurveyData}
                      userData={userData}
                      userMeta={userMeta}
                      refetchUsers={refetchUsers}
        />
    </div>
}

function ContactTable({columns, data, contactHeaders, contactMeta, supportRolesData, refetchContacts, refetchingContacts, surveyRecipients, surveyData, setSurveyData, userData, userMeta, refetchUsers}) {
    const intl = useIntl()
    const limitedEditing = !surveyData || (surveyData.status === "IN PROGRESS" || surveyData.status === "FINISHED" || surveyData.status === "ARCHIVED" || !surveyRecipients)

    function DefaultColumnFilter({column: { filterValue, setFilter }}) {
        return (
            <input
                type={"search"}
                onClick={e => e.stopPropagation()}
                value={filterValue || ''}
                onChange={e => {setFilter(e.target.value || undefined)}}
                placeholder={intl.formatMessage({id:"search", defaultMessage:"Search"})}
            />
        )
    }

    const IndeterminateCheckbox = React.forwardRef(
        ({ indeterminate, ...rest }, ref) => {
            const defaultRef = React.useRef()
            const resolvedRef = ref || defaultRef

            React.useEffect(() => {
                resolvedRef.current.indeterminate = indeterminate
            }, [resolvedRef, indeterminate])

            return <input type="checkbox" ref={resolvedRef} {...rest} />
        }
    )

    const defaultColumn = React.useMemo(() => ({
        Filter: DefaultColumnFilter
    }), [])

    // Add prefix to key to match base data
    const prefixKey = (key, prefix) => {
        if (prefix === "meta") {
            return `meta.${key}`
        }
        if (prefix === "role") {
            return `roles.${key}`
        }
        if (supportRolesData.includes(key)) {
            return `roles.${key}`
        }
        if (contactHeaders.includes(key)) {
            return `meta.${key}`
        }
        return key
    }

    const sortSection = (defaultOrder, storedOrder) => {
        const inStoredOrder = storedOrder.filter(item => defaultOrder.includes(item))
        const notInStoredOrder = defaultOrder.filter(item => !storedOrder.includes(item))
        return [...new Set([...inStoredOrder, ...notInStoredOrder])]
    }

    const pageSizeFromStorage = localStorage.getItem("pageSize") ? Number(localStorage.getItem("pageSize")) : 50

    const hiddenContactColumns = localStorage.getItem("hiddenContactColumns") ? JSON.parse(localStorage.getItem("hiddenContactColumns")).map((key)=>prefixKey(key,"meta")) : []
    const hiddenRoleColumns = localStorage.getItem("hiddenRoleColumns") ? JSON.parse(localStorage.getItem("hiddenRoleColumns")).map((key)=>prefixKey(key,"role")) : []
    const hiddenColumnsFromStorage = [...hiddenContactColumns, ...hiddenRoleColumns]

    const storedContactColumnOrder = localStorage.getItem("contactColumnOrder") ? JSON.parse(localStorage.getItem("contactColumnOrder")).map((key)=>prefixKey(key,"meta")) : []
    const storedRoleColumnOrder = localStorage.getItem("roleColumnOrder") ? JSON.parse(localStorage.getItem("roleColumnOrder")).map((key)=>prefixKey(key,"role")) : []

    const processedContactHeaders = sortSection(contactHeaders.map(header => `meta.${header}`), storedContactColumnOrder)
    const processedSupportRolesData = sortSection(supportRolesData.map(role => `roles.${role}`), storedRoleColumnOrder)

    const columnOrderFromStorage = [...processedContactHeaders, 'divider', ...processedSupportRolesData]

    const filtersFromStorage = sessionStorage.getItem("contactTableFilters") ? JSON.parse(sessionStorage.getItem("contactTableFilters")) : []

    const sortByFromStorage = sessionStorage.getItem('contactTableSortBy') ? JSON.parse(sessionStorage.getItem('contactTableSortBy')) : [];

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        prepareRow,
        selectedFlatRows,
        preGlobalFilteredRows,
        setGlobalFilter,
        state,
        state: {pageIndex, pageSize, filters, sortBy},
        setHiddenColumns,
        setColumnOrder,
        setAllFilters
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            initialState: {
                pageIndex: 0,
                pageSize: pageSizeFromStorage,
                hiddenColumns: hiddenColumnsFromStorage,
                columnOrder: columnOrderFromStorage,
                filters: filtersFromStorage,
                sortBy: sortByFromStorage
            }
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
        useRowSelect,
        useColumnOrder,
        hooks => {
            if (!limitedEditing) {
                hooks.visibleColumns.push(columns => [
                    {
                        id: 'selection',
                        width: 36,
                        Header: ({getToggleAllRowsSelectedProps}) => (
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        ),
                        Cell: ({row}) => (
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        ),
                    },
                    ...columns,
                ])
            }
        }
    )

    const hideColumns = hiddenColumns => {
        const prefixedHiddenColumns = hiddenColumns.map(prefixKey)
        setHiddenColumns(prefixedHiddenColumns)
    }

    const orderColumns = (metaColumns, roleColumns) => {
        const prefixedContactColumns = metaColumns.map(header => `meta.${header}`);
        const prefixedRoleColumns = roleColumns.map(role => `roles.${role}`);

        const sortedMetaColumns = sortSection(contactHeaders.map(header => `meta.${header}`), prefixedContactColumns);
        const sortedRoleColumns = sortSection(supportRolesData.map(role => `roles.${role}`), prefixedRoleColumns);

        const finalOrder = [...sortedMetaColumns, 'divider', ...sortedRoleColumns]

        setColumnOrder(finalOrder)
    }

    useEffect(()=>{
        sessionStorage.setItem("contactTableFilters", JSON.stringify(filters))
    },[filters])

    useEffect(() => {
        sessionStorage.setItem('contactTableSortBy', JSON.stringify(sortBy))
    }, [sortBy])

    const selectedContactsId = selectedFlatRows.map(row => row.original.id)
    const addContactsToSurvey = () => {
        let newSurveyData = { ...surveyData, contacts: surveyData.contacts || [] }
        selectedContactsId.forEach(contactId => {
            if (!newSurveyData.contacts.includes(contactId)) {
                newSurveyData.contacts.push(contactId)
            }
        });
        setSurveyData(newSurveyData)
    }

    const addContactsToSurveyFromUpload = (dataFromUpload) => {
        let contacts = surveyData?.contacts ? [...surveyData.contacts] : []

        let contactsSet = new Set(contacts)

        for (let contact of dataFromUpload) {
            if (!contactsSet.has(contact)) {
                contacts.push(contact)
                contactsSet.add(contact)
            }
        }

        setSurveyData({ ...surveyData, contacts })
    }

    const [displayLoading, setDisplayLoading] = useState(false)

    useEffect(()=>{
        if (refetchingContacts) {
            setDisplayLoading(true)
        } else {
            // If refetchingContacts ends too quickly delay state change
            const timeoutId = setTimeout(() => {
                setDisplayLoading(false)
            }, 1000)

            return () => clearTimeout(timeoutId)
        }
    },[refetchingContacts])

    return <div className={"contact-table-container"}>
        <div className={"toolbar-container"}>
            <div className={"filters"}>
                <GlobalFilter
                    preGlobalFilteredRows={preGlobalFilteredRows}
                    globalFilter={state.globalFilter}
                    setGlobalFilter={setGlobalFilter}
                    students={rows.length}
                />
                <FiltersDisplay filters={filters} removeFilters={()=>setAllFilters([])}/>
                <button className={displayLoading ? "icon-button refresh loading" : "icon-button refresh"} onClick={refetchContacts} title={intl.formatMessage({id:"refetchContacts", defaultMessage:"Update contacts"})}>
                    <RefreshIcon />
                </button>
            </div>
            <div className={"buttons"}>
                <ColumnEditor contactHeaders={contactHeaders}
                              hideColumns={hiddenColumns=>hideColumns(hiddenColumns)}
                              orderColumns={orderColumns}
                              supportRolesData={supportRolesData}
                />
                {surveyRecipients && !limitedEditing &&
                <>
                   <UploadContacts
                       addToSurvey={true}
                       addStudentsToSurvey={data => addContactsToSurveyFromUpload(data)}
                       refetchContacts={refetchContacts}
                       userData={userData}
                       supportRolesData={supportRolesData}
                       displayFullUpload={false}
                       contactMeta={contactMeta}
                       contactHeaders={contactHeaders}
                       userMeta={userMeta}
                       refetchUsers={refetchUsers}
                   />
                    <button disabled={selectedFlatRows.map(row => row.original.id).length < 1} className={"add"} onClick={()=>addContactsToSurvey()}>
                        <UpArrow/>
                        {intl.formatMessage(
                            {
                                id: 'survey.contacts.addToSurvey',
                                defaultMessage: 'Add Selected',
                            })}
                        {selectedFlatRows && selectedFlatRows.length > 0 &&
                            <> ({selectedFlatRows.length})</>}
                    </button>
                </>
                }
            </div>
        </div>
        <div className={"table-container"}>
            <table {...getTableProps()} className={`${surveyRecipients && !limitedEditing ? "checkbox-table" : ""} contact-table ${rows.length < 1 ? "no-results-table" : ""}`}>
                <thead>
                {
                    headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => (
                                <th {...column.getHeaderProps()} className={column.id === "divider" ? "divider" : ""}>
                                    <span {...column.getSortByToggleProps({title:undefined})}>
                                        {column.render('Header')}
                                    </span>
                                    {column.isSorted &&
                                        <span>
                                    {column.isSortedDesc ?
                                        ' 🔽' :
                                        ' 🔼'
                                    }
                                </span>
                                    }
                                    {column.canFilter && <div className={column.filterValue ? "filter-active filter-container": "filter-container"}>{column.render('Filter')}</div>}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                {
                    page.map(row => {
                        prepareRow(row)
                        return (
                            <tr className={surveyData?.contacts && surveyData.contacts.includes(row.original.id) ? "in-survey" : ""} {...row.getRowProps()}>
                                {row.cells.map(cell => {
                                    return <td className={cell.column.id === "divider" ? "divider" : ""} title={String(cell.value)} {...cell.getCellProps()}>
                                        {cell.render('Cell')}
                                    </td>
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
            {rows.length < 1 &&
                <div className={"no-results"}>{intl.formatMessage({id:'noResults', defaultMessage:'No results with given search term'})}</div>
            }
        </div>
        <div className="pagination">
            <select
                value={pageSize}
                onChange={e => {
                    setPageSize(Number(e.target.value))
                    localStorage.setItem("pageSize", e.target.value)
                }}
            >
                {[10, 50, 100, 200, 500].map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                        {intl.formatMessage(
                            {
                                id: 'surveys.recipients.table.show',
                                defaultMessage: 'Show',
                            })}{' '}
                        {pageSize}
                    </option>
                ))}
            </select>
            <div className={"page-index"}>
                {intl.formatMessage(
                    {
                        id: 'surveys.recipients.table.page',
                        defaultMessage: 'Page',
                    })}{' '}
                <strong>
                    {pageIndex + 1}{' '}
                    {intl.formatMessage(
                        {
                            id: 'surveys.recipients.table.of',
                            defaultMessage: 'of',
                        })}
                    {' '}{pageOptions.length}
                </strong>
            </div>
            <div className={"buttons"}>
                <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                    <First/>
                </button>{' '}
                <button onClick={() => previousPage()} disabled={!canPreviousPage}>
                    <Previous/>
                </button>{' '}
                <button onClick={() => nextPage()} disabled={!canNextPage}>
                    <Next />
                </button>{' '}
                <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                    <Last/>
                </button>
            </div>
        </div>
    </div>
}

export function FiltersDisplay({filters, removeFilters}) {
    const intl = useIntl()
    const headerName = (column) => {
        // TODO: fix this hack by using a function in the table data accessor
        let string = column
        if (column.startsWith("meta.")) {
            string = string.replace("meta.","contactmeta.")
        }
        if (column.startsWith("roles.")) {
            string = string.replace("roles.","role.")
        }
        return intl.formatMessage({
            id: `${string}`,
            defaultMessage: string
        })
    }
    const filterText = intl.formatMessage({
            id: 'contact.activeFilters',
            defaultMessage: 'Filters: ',
        }).concat(filters.map(e => headerName(e.id)).join(', '))

    if (filters.length > 0) {
        return <button className={"remove-filters"} title={filterText} onClick={()=>removeFilters()}>{intl.formatMessage({
            id: "contact.removeFilters",
            defaultMessage: "Remove filters"
        })}
            <CloseIcon />
        </button>
    }
    return null
}

export function ColumnEditor({contactHeaders, hideColumns, orderColumns, userColumns, supportRolesData}) {
    const [showEditorPopup, setShowEditorPopup] = useState(false)
    const intl = useIntl()

    return <>
        <button className={"icon-button"} onClick={()=>setShowEditorPopup(true)}>
            <MoreIcon />
        </button>
        {showEditorPopup &&
        <Popup closePopup={()=>setShowEditorPopup(false)}>
            <h2>{intl.formatMessage({
                id: 'contact.editColumns',
                defaultMessage: 'Edit table columns',
            })}
            </h2>
            <Columns contactHeaders={contactHeaders}
                     closePopup={()=>setShowEditorPopup(false)}
                     hideColumns={hiddenColumns=>hideColumns(hiddenColumns)}
                     orderColumns={orderColumns}
                     userColumns={userColumns}
                     supportRolesData={supportRolesData}
            />
        </Popup>
        }
    </>
}

function Columns({closePopup, contactHeaders, hideColumns, orderColumns, userColumns, supportRolesData}) {
    const intl = useIntl()
    const getItemFromLocalStorage = (key, validItems) => {
        const item = localStorage.getItem(key)
        let result = item ? JSON.parse(item) : []
        // Filter out any columns not set in codes
        if (validItems) {
            result = result.filter(item => validItems.includes(item))
        }
        return result
    }

    const processColumnData = (hiddenKey, orderKey, defaultHeaders) => {
        const initialHiddenColumns = getItemFromLocalStorage(hiddenKey, defaultHeaders)
        const columnOrderFromStorage = getItemFromLocalStorage(orderKey, defaultHeaders)
        const orderedHeaders = [...new Set([...columnOrderFromStorage, ...defaultHeaders])]

        return [initialHiddenColumns, orderedHeaders]
    }

    const [initialUserHiddenColumns, userOrderedHeaders] =
        processColumnData(userColumns ? "hiddenUserColumns" : "hiddenContactColumns",
            userColumns ? "userColumnOrder" : "contactColumnOrder",
            contactHeaders)

    const [hiddenColumns, setHiddenColumns] = useState(initialUserHiddenColumns)
    const [columnOrder, setColumnOrder] = useState(userOrderedHeaders)

    let initialHiddenRoleColumns, orderedRoleHeaders
    if (!userColumns) {
        [initialHiddenRoleColumns, orderedRoleHeaders] =
            processColumnData("hiddenRoleColumns", "roleColumnOrder", supportRolesData)
    }

    // Initialize these regardless of userColumns, but they'll only have values if userColumns is false
    const [hiddenRoleColumns, setHiddenRoleColumns] = useState(initialHiddenRoleColumns)
    const [roleColumnOrder, setRoleColumnOrder] = useState(orderedRoleHeaders)

    const savePreferences = () => {
        if (hiddenRoleColumns) {
            localStorage.setItem("hiddenRoleColumns", JSON.stringify(hiddenRoleColumns))
        }
        if (roleColumnOrder) {
            localStorage.setItem("roleColumnOrder", JSON.stringify(roleColumnOrder))
        }
        if (userColumns) {
            localStorage.setItem("userColumnOrder", JSON.stringify(columnOrder))
            localStorage.setItem("hiddenUserColumns", JSON.stringify(hiddenColumns))
        } else {
            localStorage.setItem("contactColumnOrder", JSON.stringify(columnOrder))
            localStorage.setItem("hiddenContactColumns", JSON.stringify(hiddenColumns))
        }
        hideColumns([...hiddenColumns, ...(hiddenRoleColumns || [])])
        if (userColumns) {
            orderColumns(columnOrder)
        } else {
            orderColumns(columnOrder, roleColumnOrder)
        }
        closePopup()
    }

    const toggleColumnSelection = (column, roleColumn) => {
        if (roleColumn) {
            if (hiddenRoleColumns.includes(column)) {
                setHiddenRoleColumns(hiddenRoleColumns.filter(selected => selected !== column))
            } else {
                setHiddenRoleColumns( hiddenRoleColumns => [...hiddenRoleColumns, column])
            }
        } else {
            if (hiddenColumns.includes(column)) {
                setHiddenColumns(hiddenColumns.filter(selected => selected !== column))
            } else {
                setHiddenColumns( hiddenColumns => [...hiddenColumns, column])
            }
        }
    }

    const changeOrder = (to, from, roleColumn) => {
        if (roleColumn) {
            let newOrder = [...roleColumnOrder]
            newOrder.splice(to, 0, newOrder.splice(from, 1)[0])
            setRoleColumnOrder(newOrder)
        } else {
            let newOrder = [...columnOrder]
            newOrder.splice(to, 0, newOrder.splice(from, 1)[0])
            setColumnOrder(newOrder)
        }
    }

    const [columnToChangePlace, setColumnToChangePlace] = useState(null)
    const [activeDragOver, setActiveDragOver] = useState(null)

    const [roleColumnToChangePlace, setRoleColumnToChangePlace] = useState(null)
    const [activeRoleDragOver, setActiveRoleDragOver] = useState(null)

    const [draggingRoleColumn, setDraggingRoleColumn] = useState(false)

    const setNewPlaceForColumn = (roleColumn) => {
        if (roleColumn) {
            if (activeRoleDragOver !== roleColumnToChangePlace) {
                changeOrder(activeRoleDragOver, roleColumnToChangePlace, roleColumn)
            }
        } else {
            if (activeDragOver !== columnToChangePlace) {
                changeOrder(activeDragOver, columnToChangePlace)
            }
        }
    }

    const changeOrderEnd = (roleColumn) => {
        if (roleColumn) {
            setRoleColumnToChangePlace(null)
            setActiveRoleDragOver(null)
        } else {
            setColumnToChangePlace(null)
            setActiveDragOver(null)
        }
    }

    const dragOrderStart = (e,i, roleColumn) => {
        e.dataTransfer.effectAllowed = "move"
        setDraggingRoleColumn(roleColumn)
        if (roleColumn) {
            setRoleColumnToChangePlace(i)
        } else {
            setColumnToChangePlace(i)
        }
    }

    const dragOverHandler = (e,i) => {
        e.preventDefault()
        e.dataTransfer.effectAllowed = "move"
    }

    const dragEnterHandle = (e,i, roleColumn) => {
        e.preventDefault()
        if (draggingRoleColumn) {
            if (roleColumn) {
                setActiveRoleDragOver(i)
            }
        } else {
            if (!roleColumn) {
                setActiveDragOver(i)
            }
        }
    }

    const dragClass = (i, roleColumn) => {
        let className = ""
        if (roleColumn) {
            if (i === activeRoleDragOver && activeRoleDragOver !== roleColumnToChangePlace) {
                className = "active"
                if (activeRoleDragOver > roleColumnToChangePlace) {
                    className = "active-bottom"
                }
            }
        } else {
            if (i === activeDragOver && activeDragOver !== columnToChangePlace) {
                className = "active"
                if (activeDragOver > columnToChangePlace) {
                    className = "active-bottom"
                }
            }
        }

        return className
    }

    return <>
        <h4>{intl.formatMessage({defaultMessage:'Student Information', id:'studentInformation'})}</h4>
        <ul className={"column-headers-list"}>
            {columnOrder.map((column, i) => {
                return <li key={i} className={dragClass(i)} draggable={true}
                           onDragStart={(e)=>dragOrderStart(e,i)}
                           onDragEnd={()=>changeOrderEnd()}
                           onDragEnter={e=>dragEnterHandle(e,i)}
                           onDragOver={e=>dragOverHandler(e,i)}
                           onDrop={()=>setNewPlaceForColumn()}
                >
                    <input type={"checkbox"} id={"column"+i} name={"column"+i}
                        checked={!hiddenColumns.includes(column)} onChange={()=>toggleColumnSelection(column)}
                    />
                    <label htmlFor={"column"+i}>
                        {intl.formatMessage({
                            id: `contactmeta.${column}`,
                            defaultMessage: column
                        })}</label>
                    <div className={"order-buttons"}>
                        {i !== 0 ?
                        <button onClick={() => changeOrder(i - 1, i)}><UpArrow/></button> :
                            <button disabled><UpArrow/></button>
                        }
                        {i+1 !== columnOrder.length ?
                        <button onClick={()=>changeOrder(i+1, i)}><DownArrow/></button> :
                            <button disabled><DownArrow/></button>
                        }
                    </div>

                </li>
            })}
        </ul>
        {roleColumnOrder &&
        <>
            <h4>{intl.formatMessage({defaultMessage:'Assigned Roles', id:'assignedRoles'})}</h4>
            <ul className={"column-headers-list"}>
                {roleColumnOrder.map((column, i) => {
                    return <li key={i} className={dragClass(i, true)} draggable={true}
                               onDragStart={(e)=>dragOrderStart(e,i, true)}
                               onDragEnd={()=>changeOrderEnd(true)}
                               onDragEnter={e=>dragEnterHandle(e,i,true)}
                               onDragOver={e=>dragOverHandler(e,i)}
                               onDrop={()=>setNewPlaceForColumn(true)}
                    >
                        <input type={"checkbox"} id={"role-column"+i} name={"role-column"+i}
                               checked={!hiddenRoleColumns.includes(column)} onChange={()=>toggleColumnSelection(column, true)}
                        />
                        <label htmlFor={"role-column"+i}>
                            {intl.formatMessage({
                                id: "role."+column,
                                defaultMessage: column
                            })}
                                </label>
                        <div className={"order-buttons"}>
                            {i !== 0 ?
                                <button onClick={() => changeOrder(i - 1, i, true)}><UpArrow/></button> :
                                <button disabled><UpArrow/></button>
                            }
                            {i+1 !== roleColumnOrder.length ?
                                <button onClick={()=>changeOrder(i+1, i, true)}><DownArrow/></button> :
                                <button disabled><DownArrow/></button>
                            }
                        </div>

                    </li>
                })}
            </ul>
        </>
        }

        <div className={"options"}>
            <button className={"cancel"} onClick={()=>closePopup()}>
                {intl.formatMessage({
                    id: 'modal.cancel',
                    defaultMessage: 'Cancel'})}
            </button>
            <button className="confirm" onClick={()=>savePreferences()}>{intl.formatMessage({
                id: 'survey.unsavedChanges.confirm',
                defaultMessage: 'Save changes',
            })}</button>
        </div>
    </>
}